import { of } from 'rxjs'
import { Arn } from 'src/utils/types'
import { getResourceTypeDetails, ResourceType, ResourceVariant } from 'src/utils/resource-types'
import { Application } from 'src/app/account/services/application.service'
import {
  AccessTemplatesDto,
  ApplicationType,
  JobDto,
  JobStatus,
  RecoveryPointAttemptDto,
  RecoveryPointDto,
  SyncPairAccessDto,
  SyncPhaseDto,
} from 'src/app/services/dtos'
import { AccessState } from 'src/app/services/access.service'
import { ViewableIssue } from 'src/app/account/components/issues-view/issues-view.component'
import {
  ResourceStatus,
  ViewableApplicationResource,
} from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { AwsEventLogAction, AwsEventLogEntry } from 'src/app/interfaces'
import { getResourceTypeDisplayString } from 'src/app/shared/utils'
import { ResourcesTab } from 'src/app/account/components/application/resources-tab'
import {
  BackupHealthStatus,
  FailbackStatus,
  RecoveryEnvironmentStatus,
  STATUS_TEXT,
} from 'src/app/account/components/application/statusText'
import { AppStatusState } from 'src/app/account/components/process-state/process-state.component'
import {
  AcmCertificateNotFoundIssue,
  AmiStorageNotAccessibleIssue,
  BaseIssue,
  BucketBackfillFailedIssue,
  ClientErrorIssue,
  DeleteFailedIssue,
  DetailedIssue,
  DrsAgentNotFoundIssue,
  EksClusterAccessDeniedIssue,
  EksRequiredEndpointInaccessibleIssue,
  EndpointInaccessibleIssue,
  FailbackResourceIssue,
  InfectedFiles,
  KeyPairNotFoundIssue,
  MalwareScanIssue,
  MarketplaceActionRequiredIssue,
  MarketplaceAmiCantBeCopiedIssue,
  MarketplaceInstanceCantBeCopiedIssue,
  MultipleMatchingImagesIssue,
  NewTargetPlanRequiredIssue,
  PersistentIssue,
  QuotaIssue,
  RaisedIssueScope,
  RefreshClientErrorIssue,
  ResourcePolicyDeniedIssue,
  S3BucketReplicationAlreadyConfiguredIssue,
  S3BucketReplicationNotConfiguredIssue,
  S3RecoveryBucketNameConflictIssue,
  ServiceControlPolicyPermissionIssue,
  SubscriptionOverageIssue,
  SyncPairPermissionIssue,
  UnconfiguredAzureBlobContainerIssue,
  UnconfiguredS3BucketReplicationIssue,
  UnhandledRouteTargetIssue,
  UninstallDrsAgentIssue,
} from 'src/app/account/services/issue'

let issueCounter = 0

function vi(
  issue: BaseIssue,
  resourceType: ResourceType,
  scope: RaisedIssueScope,
  resourceName: string = 'Resource Name',
  // Override the derived ARN. Needed for the duplicate-scenario case where two
  // distinct resources (different ARNs) share an identical display name.
  arnOverride?: string
): ViewableIssue {
  issueCounter++
  return {
    arn: (arnOverride ??
      `arn:aws:mock:us-east-1:123456789012:${resourceType}/${resourceName.replace(/\s/g, '-').toLowerCase()}`) as Arn,
    issue,
    issueId: `test-issue-${issueCounter}`,
    resourceName,
    resourceType,
    resourceVariant: null,
    scope,
    issueDto: null,
    deleter$: () => of(null),
    timestamp: new Date(),
  } as ViewableIssue
}

export function buildAllMockIssues(): ViewableIssue[] {
  issueCounter = 0
  const issues: ViewableIssue[] = []

  // 1. FailbackResourceIssue (existing)
  const failbackIssue = new FailbackResourceIssue(
    'This message comes from the API and tells you why Arpio could not do the thing.',
    'arn:....' as Arn,
    'resourceId',
    ResourceType.ec2Instance,
    new Date()
  )
  issues.push({
    ...vi(failbackIssue, ResourceType.ec2Instance, RaisedIssueScope.FAILBACK),
    timestamp: failbackIssue.timestamp,
  })

  // 2. UnconfiguredS3BucketReplicationIssue (existing)
  issues.push(
    vi(
      new UnconfiguredS3BucketReplicationIssue(
        'my-data-bucket',
        'Bucket replication has not been configured for this bucket.'
      ),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'my-data-bucket'
    )
  )

  // 3. S3BucketReplicationNotConfiguredIssue (existing)
  issues.push(
    vi(
      new S3BucketReplicationNotConfiguredIssue(
        'bucketName',
        'arn:aws:iam::123456:role/replication-role',
        'arn:aws:s3:::target-bucket',
        'arn:aws:kms:us-east-1:123456:key/abc-123',
        'Please configure replication for this bucket.'
      ),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'bucketName'
    )
  )

  // 4. BucketBackfillFailedIssue (existing)
  issues.push(
    vi(
      new BucketBackfillFailedIssue(
        ['s3://bucket/object1', 's3://bucket/object2'],
        100,
        2,
        'Some objects failed to copy.'
      ),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'backfill-bucket'
    )
  )

  // 5. DrsAgentNotFoundIssue (existing)
  issues.push(
    vi(
      new DrsAgentNotFoundIssue('DRS agent not found for this instance.', 'hw-abc123', false),
      ResourceType.ec2Instance,
      RaisedIssueScope.BACKUP,
      'i-0abc123def'
    )
  )

  // 6. EksClusterAccessDeniedIssue (existing)
  issues.push(
    vi(
      new EksClusterAccessDeniedIssue(
        'Arpio does not have access to EKS Cluster digitata1-rds. Please ensure IAM Role arn:aws:iam::862237587541:role/ArpioPrimaryK8sDel-HFLOkjq8Bhv1MfFPb6wi9g-us-east-1 is in the kube-system aws-auth ConfigMap.',
        'digitata1-rds',
        'arn:aws:iam::862237587541:role/ArpioPrimaryK8sDel-HFLOkjq8Bhv1MfFPb6wi9g-us-east-1',
        'us-east-1',
        'failure to get dynamic client for cluster_name - 401:Unauthorized'
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'digitata1-rds'
    )
  )

  // 7. EndpointInaccessibleIssue (existing)
  issues.push(
    vi(
      new EndpointInaccessibleIssue(
        'Arpio failed to connect to the AWS API at endpoint "https://s3.us-east-1.amazonaws.com/arpioprimaryaccess-7vgfhhye2-primaryscratchbucket-1b9k059oi3rh1/eks/priv-test/d9c8a6bc-9a5a-4b90-8112-ea5c241d9a69.enc".',
        'https://s3.us-east-1.amazonaws.com/arpioprimaryaccess-7vgfhhye2-primaryscratchbucket-1b9k059oi3rh1/eks/priv-test/d9c8a6bc-9a5a-4b90-8112-ea5c241d9a69.enc'
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'priv-test'
    )
  )

  // 8. EksRequiredEndpointInaccessibleIssue (existing)
  issues.push(
    vi(
      new EksRequiredEndpointInaccessibleIssue(
        'Arpio failed to connect to the AWS API at endpoint "https://s3.us-east-1.amazonaws.com/...".',
        'https://s3.us-east-1.amazonaws.com/arpioprimaryaccess-7vgfhhye2-primaryscratchbucket-1b9k059oi3rh1/eks/priv-test/d9c8a6bc.enc',
        's3',
        ['subnet-abcde', 'subnet-foobar']
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'priv-test'
    )
  )

  // 9. DetailedIssue (existing)
  issues.push(
    vi(
      new DetailedIssue(
        "A certificate for 'hotel-chcom-dxapi-prod.dxprod.cloud.chotel.com' could not be found in the recovery environment. Certificates need to be manually provisioned in the recovery environment for Arpio to use.",
        'Detailed Issue Title',
        'Original Error',
        'additional_details_title',
        'additional_details_url',
        true
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'Detailed Issue 1'
    )
  )

  issues.push(
    vi(
      new DetailedIssue(
        'Please run one of the following commands to fix things.',
        'Detailed Issue Title',
        null,
        null,
        null,
        false,
        {
          bash: 'aws eks update-kubeconfig --name my-cluster --region us-west-2\nkubectl apply -f https://raw.githubusercontent.com/arpio/arpio-agent/main/deploy.yaml\nkubectl get pods -n arpio-system',
          powershell:
            'aws eks update-kubeconfig --name my-cluster --region us-west-2\nkubectl apply -f https://raw.githubusercontent.com/arpio/arpio-agent/main/deploy.yaml\nGet-Process | Where-Object { $_.Name -eq "arpio-agent" }',
        }
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'Detailed Issue 2'
    )
  )

  // --- New issue types below ---

  // 10. PersistentIssue
  issues.push(
    vi(
      new PersistentIssue('This is a persistent notification that remains visible until manually dismissed.'),
      ResourceType.lambdaFunction,
      RaisedIssueScope.BACKUP,
      'my-lambda-function'
    )
  )

  // 11. DeleteFailedIssue
  issues.push(vi(new DeleteFailedIssue(), ResourceType.ec2Instance, RaisedIssueScope.CLEANUP, 'i-0delete123'))

  // 12. AcmCertificateNotFoundIssue
  issues.push(
    vi(
      new AcmCertificateNotFoundIssue(
        "A certificate for 'api.example.com' could not be found in the recovery environment.",
        'arn:aws:acm:us-east-1:123456:certificate/abc-123',
        'api.example.com'
      ),
      ResourceType.acmCertificate,
      RaisedIssueScope.RESTORE,
      'api.example.com'
    )
  )

  // 13. ClientErrorIssue
  issues.push(
    vi(
      new ClientErrorIssue(
        'An error occurred while modifying the DB instance.',
        'InvalidParameterCombination',
        'The DB instance class db.t3.micro does not support encryption.',
        'ModifyDBInstance'
      ),
      ResourceType.rdsDbInstance,
      RaisedIssueScope.RESTORE,
      'my-database'
    )
  )

  // 14. QuotaIssue
  issues.push(
    vi(
      new QuotaIssue(
        'You have exceeded the maximum number of EC2 instances allowed in this region.',
        'InstanceLimitExceeded',
        'Your quota allows for 0 more running instance(s).',
        'RunInstances'
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.RESTORE,
      'i-0quota456'
    )
  )

  // 15. MultipleMatchingImagesIssue
  const matchCriteria = new Map<string, Array<string>>()
  matchCriteria.set('name', ['my-ami-*'])
  matchCriteria.set('tag:Environment', ['production'])
  issues.push(
    vi(
      new MultipleMatchingImagesIssue(
        'Multiple AMIs match the search criteria. Please ensure only one AMI matches.',
        matchCriteria
      ),
      ResourceType.ec2Image,
      RaisedIssueScope.SCAN,
      'ami-0multi123'
    )
  )

  // 16. MarketplaceAmiCantBeCopiedIssue
  const amiMatchCriteria = new Map<string, Array<string>>()
  amiMatchCriteria.set('name', ['marketplace-ami-*'])
  issues.push(
    vi(
      new MarketplaceAmiCantBeCopiedIssue(
        'This AMI is an AWS Marketplace product and cannot be copied across accounts.',
        ['prod-abc123'],
        amiMatchCriteria
      ),
      ResourceType.ec2Image,
      RaisedIssueScope.RESTORE,
      'ami-marketplace-01'
    )
  )

  // 17. MarketplaceInstanceCantBeCopiedIssue
  issues.push(
    vi(
      new MarketplaceInstanceCantBeCopiedIssue(
        'This instance uses an AWS Marketplace AMI and cannot be directly copied.'
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.RESTORE,
      'i-0marketplace789'
    )
  )

  // 18. AmiStorageNotAccessibleIssue
  const storageMatchCriteria = new Map<string, Array<string>>()
  storageMatchCriteria.set('name', ['encrypted-ami-*'])
  issues.push(
    vi(
      new AmiStorageNotAccessibleIssue(
        'The AMI storage is encrypted with a key that is not accessible in the recovery region.',
        storageMatchCriteria
      ),
      ResourceType.ec2Image,
      RaisedIssueScope.RESTORE,
      'ami-encrypted-01'
    )
  )

  // 19. UnhandledRouteTargetIssue
  issues.push(
    vi(
      new UnhandledRouteTargetIssue(
        'Route table contains a route target that Arpio does not know how to handle.',
        'arn:aws:ec2:us-east-1:123456:route-table/rtb-abc123',
        'vpce-svc-abc123'
      ),
      ResourceType.ec2RouteTable,
      RaisedIssueScope.BACKUP,
      'rtb-abc123'
    )
  )

  // 20. MalwareScanIssue (infected)
  issues.push(
    vi(
      new MalwareScanIssue(
        'infected',
        'scan-abc123',
        'Malware was detected during a scan of the EBS volume.',
        'https://console.aws.amazon.com/guardduty',
        [new InfectedFiles('Trojan.GenericKD', 3, ['/var/log/malware.exe', '/tmp/payload.bin', '/opt/bad.sh'])]
      ),
      ResourceType.ecsService,
      RaisedIssueScope.RESTORE,
      'my-ecs-service'
    )
  )

  // 21. MalwareScanIssue (failed)
  issues.push(
    vi(
      new MalwareScanIssue(
        'failed',
        'scan-def456',
        'The malware scan could not be completed due to an internal error.',
        'https://console.aws.amazon.com/guardduty',
        []
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.RESTORE,
      'i-0scanfail789'
    )
  )

  // 22. MarketplaceActionRequiredIssue
  issues.push(
    vi(
      new MarketplaceActionRequiredIssue(
        'The recovery account must subscribe to this AWS Marketplace product before recovery can proceed.',
        'https://aws.amazon.com/marketplace/pp/prodview-abc123'
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.RESTORE,
      'i-0marketplace-action'
    )
  )

  // 23. RefreshClientErrorIssue
  issues.push(
    vi(
      new RefreshClientErrorIssue(
        'An error occurred while refreshing the DynamoDB table.',
        'ResourceNotFoundException',
        'Requested resource not found: Table: my-table not found',
        'DescribeTable',
        'dynamoDbTable'
      ),
      ResourceType.dynamoDbTable,
      RaisedIssueScope.SCAN,
      'my-dynamo-table'
    )
  )

  // 24. KeyPairNotFoundIssue
  issues.push(
    vi(new KeyPairNotFoundIssue('my-ec2-keypair'), ResourceType.ec2Instance, RaisedIssueScope.BACKUP, 'i-0keypair123')
  )

  // 25. S3BucketReplicationAlreadyConfiguredIssue
  issues.push(
    vi(
      new S3BucketReplicationAlreadyConfiguredIssue('already-replicated-bucket'),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'already-replicated-bucket'
    )
  )

  // 26. S3RecoveryBucketNameConflictIssue
  issues.push(
    vi(
      new S3RecoveryBucketNameConflictIssue(
        'primary-data-bucket',
        'primary-data-bucket-recovery',
        'The recovery bucket name conflicts with an existing bucket in the recovery account.'
      ),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'primary-data-bucket'
    )
  )

  // 27. UninstallDrsAgentIssue
  issues.push(
    vi(
      new UninstallDrsAgentIssue('s-abc123def', 'i-0drsuninstall'),
      ResourceType.ec2Instance,
      RaisedIssueScope.CLEANUP,
      'i-0drsuninstall'
    )
  )

  // 28. SyncPairPermissionIssue (general - no IAM operation)
  // Note: The dedicated sub-component requires a working syncPair to render its update-access dialog.
  // It still renders the accordion header + message via the dedicated component.
  issues.push(
    vi(
      new SyncPairPermissionIssue(
        'Arpio does not have permission to describe load balancers in the recovery account. Please update access templates.',
        'DescribeLoadBalancers'
      ),
      ResourceType.elbLoadBalancer,
      RaisedIssueScope.RESTORE,
      'my-load-balancer'
    )
  )

  // 29. SyncPairPermissionIssue (IAM operation - triggers IAM variant)
  // Note: The iam-permission-issue sub-component subscribes to syncPair.manualResourceTemplateUrl$ in ngOnInit.
  // With a fake syncPair this may error but should not prevent rendering.
  issues.push(
    vi(
      new SyncPairPermissionIssue(
        'Arpio does not have permission to create IAM roles in the recovery account. Please grant the CreateRole permission.',
        'CreateRole'
      ),
      ResourceType.iamRole,
      RaisedIssueScope.RESTORE,
      'arpio-recovery-role'
    )
  )

  // 30. ResourcePolicyDeniedIssue
  issues.push(
    vi(
      new ResourcePolicyDeniedIssue(
        'Access to this secret is explicitly denied by a resource-based policy. Please update the policy to allow Arpio access.'
      ),
      ResourceType.secretsManagerSecret,
      RaisedIssueScope.RESTORE,
      'prod/database/credentials'
    )
  )

  // 31. ServiceControlPolicyPermissionIssue
  issues.push(
    vi(
      new ServiceControlPolicyPermissionIssue(
        'Access to this KMS key is denied by a Service Control Policy in your AWS Organization. Please update the SCP to allow this operation.'
      ),
      ResourceType.kmsKey,
      RaisedIssueScope.RESTORE,
      'alias/my-encryption-key'
    )
  )

  // 32. SubscriptionOverageIssue
  issues.push(
    vi(
      new SubscriptionOverageIssue(
        'Your current subscription allows protection of up to 50 resources. You are currently protecting 63 resources. Please contact sales to increase your subscription.'
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.BACKUP,
      'subscription-limit'
    )
  )

  // 33. NewTargetPlanRequiredIssue
  issues.push(
    vi(
      new NewTargetPlanRequiredIssue(
        'Availability zones have changed in the recovery region. A new subnet-AZ mapping is required.',
        [
          { srcSubnetId: 'subnet-src1', tgtSubnetId: 'subnet-tgt1', oldAz: 'us-west-2a', newAz: 'us-west-2b' },
          { srcSubnetId: 'subnet-src2', tgtSubnetId: 'subnet-tgt2', oldAz: 'us-west-2b', newAz: 'us-west-2c' },
        ]
      ),
      ResourceType.ec2Subnet,
      RaisedIssueScope.RESTORE,
      'subnet-src1'
    )
  )

  // 34. UnconfiguredAzureBlobContainerIssue
  // Note: The dedicated sub-component calls syncPair/application.getSyncPairResourceConfig$ in ngOnInit.
  // With a fake syncPair this may produce an API error but the form should still render.
  issues.push(
    vi(
      new UnconfiguredAzureBlobContainerIssue(
        'mystorageaccount',
        'mycontainer',
        'Blob container replication has not been configured. Please configure replication settings.'
      ),
      ResourceType.azureBlobContainer,
      RaisedIssueScope.BACKUP,
      'mystorageaccount/mycontainer'
    )
  )

  return issues
}

// ARP-7238 scenario 1 — duplicate resource rows. The same resource (identical
// ARN) is raised three times for one problem. KeyPairNotFound groups all
// instances into a single content sub-group, so the three land together. The
// backend appends issues without deduping, so pre-fix this rendered three
// identical rows; dedupeIssuesByArn collapses them to the most recent one.
export function buildDuplicateRowsScenario(): ViewableIssue[] {
  issueCounter = 0
  const issues: ViewableIssue[] = []
  const dupArn = 'arn:aws:ec2:us-east-1:123456789012:instance/i-0duplicateinstance' as Arn
  for (let i = 0; i < 3; i++) {
    issues.push({
      ...vi(
        new KeyPairNotFoundIssue('shared-prod-keypair'),
        ResourceType.ec2Instance,
        RaisedIssueScope.BACKUP,
        'i-0duplicateinstance',
        dupArn
      ),
      // Stagger timestamps so the dedup "keep most recent" path is exercised.
      timestamp: new Date(Date.now() - i * 60_000),
    } as ViewableIssue)
  }
  return issues
}

// ARP-7238 scenario 2 — identical names break the diff-highlight. Two distinct
// resources (different ARNs) share one display name. Same content sub-group +
// same resource type drives the diff-highlight, whose common prefix/suffix
// overlap on identical names, so pre-fix the name rendered concatenated/tripled.
// Different ARNs mean dedup leaves both rows, so the highlight clamp is what
// keeps each name rendering once.
export function buildTripledNameScenario(): ViewableIssue[] {
  issueCounter = 0
  const issues: ViewableIssue[] = []
  const sharedCertName = 'shared.example.com'
  for (const id of ['aaaa1111', 'bbbb2222']) {
    issues.push(
      vi(
        new AcmCertificateNotFoundIssue(
          `A certificate for '${sharedCertName}' could not be found in the recovery environment.`,
          `arn:aws:acm:us-east-1:123456789012:certificate/${id}`,
          sharedCertName
        ),
        ResourceType.acmCertificate,
        RaisedIssueScope.RESTORE,
        sharedCertName,
        `arn:aws:acm:us-east-1:123456789012:certificate/${id}`
      )
    )
  }
  return issues
}

export function buildStatusResources(): ViewableApplicationResource[] {
  const resourceTypes = [
    ResourceType.ec2Instance,
    ResourceType.rdsDbInstance,
    ResourceType.s3Bucket,
    ResourceType.lambdaFunction,
    ResourceType.dynamoDbTable,
    ResourceType.ecsService,
    ResourceType.eksCluster,
    ResourceType.elbLoadBalancer,
    ResourceType.secretsManagerSecret,
  ]

  const statuses = Object.values(ResourceStatus).filter((v) => !isNaN(Number(v))) as ResourceStatus[]

  return statuses.map((stat, index) => {
    const rt = resourceTypes[index % resourceTypes.length]
    return {
      resourceType: rt,
      resourceVariant: null,
      resourceArn: `arn:aws:mock:us-east-1:123456789012:resource/${index}` as Arn,
      sourceResourceName: `source-${ResourceStatus[stat].toLowerCase()}`,
      targetResourceName: `target-${ResourceStatus[stat].toLowerCase()}`,
      resourceProperties: [],
      status: stat,
      progress: 0.2,
      message: `Status: ${ResourceStatus[stat]}`,
      issues: new Set(),
      displayResourceType: getResourceTypeDisplayString(rt, null),
      resourceTypeDetails: getResourceTypeDetails({ resourceType: rt }),
    } as ViewableApplicationResource
  })
}

// -------------------------------------------------------------------------
// Parent–child resource group scenarios for visual verification
//
// Each scenario is a top-level parent (cluster / DB cluster / ASG) plus children
// linked by parentArn. They mirror the _resolveParentNeedsAttention unit tests so
// the "Needs attention" rollup vs. the parent's own status pill can be verified
// visually without provisioning real resources. Rendered in BOTH Recovery and
// Backup contexts on /test_page — the trigger sets differ (UNSATISFIED_DEPENDENCY
// is recovery-only), so scenario 4b diverges between the two tables.
// -------------------------------------------------------------------------

interface MockResourceSpec {
  type: ResourceType
  variant?: ResourceVariant
  status: ResourceStatus
  // Children link to this resource via parentArn. Build the tree top-down.
  children?: MockResourceSpec[]
}

function flattenScenario(
  spec: MockResourceSpec,
  prefix: string,
  parentArn: Arn | null,
  out: ViewableApplicationResource[]
): void {
  const variant = spec.variant ?? null
  const arn = `arn:mock:parent-child:${prefix}` as Arn
  const isTrigger =
    spec.status === ResourceStatus.FAILED ||
    spec.status === ResourceStatus.NOT_PROCESSED ||
    spec.status === ResourceStatus.UNSATISFIED_DEPENDENCY ||
    spec.status === ResourceStatus.SCAN_INFECTED ||
    spec.status === ResourceStatus.SCAN_FAILED
  out.push({
    resourceType: spec.type,
    resourceVariant: variant,
    resourceArn: arn,
    parentArn: parentArn ?? undefined,
    sourceResourceName: prefix,
    targetResourceName: prefix,
    resourceProperties: [],
    status: spec.status,
    progress: 0.4,
    // hasIssues drives the per-row issue icon and the parent's issue-count rollup.
    hasIssues: isTrigger,
    message: `Status: ${ResourceStatus[spec.status]}`,
    issues: new Set(),
    displayResourceType: getResourceTypeDisplayString(spec.type, variant as ResourceVariant),
    resourceTypeDetails: getResourceTypeDetails({
      resourceType: spec.type,
      resourceVariant: variant as ResourceVariant,
    }),
  } as ViewableApplicationResource)
  spec.children?.forEach((child, i) => flattenScenario(child, `${prefix}-${i}`, arn, out))
}

export function buildParentChildScenarioResources(): ViewableApplicationResource[] {
  const aks = (status: ResourceStatus, children?: MockResourceSpec[]): MockResourceSpec => ({
    type: ResourceType.azureAksCluster,
    status,
    children,
  })
  const eks = (status: ResourceStatus, children?: MockResourceSpec[]): MockResourceSpec => ({
    type: ResourceType.eksCluster,
    status,
    children,
  })
  const ns = (status: ResourceStatus, children?: MockResourceSpec[]): MockResourceSpec => ({
    type: ResourceType.k8sResource,
    variant: ResourceVariant.k8sNamespace,
    status,
    children,
  })
  const deploy = (status: ResourceStatus): MockResourceSpec => ({
    type: ResourceType.k8sResource,
    variant: ResourceVariant.k8sDeployment,
    status,
  })
  const nodePool = (status: ResourceStatus, children?: MockResourceSpec[]): MockResourceSpec => ({
    type: ResourceType.azureAksNodePool,
    status,
    children,
  })
  const dbInstance = (status: ResourceStatus): MockResourceSpec => ({
    type: ResourceType.rdsDbInstance,
    status,
  })
  const ec2 = (status: ResourceStatus): MockResourceSpec => ({ type: ResourceType.ec2Instance, status })

  // [scenarioPrefix, tree] — prefix becomes the source/target name so rows self-describe.
  const scenarios: [string, MockResourceSpec][] = [
    // 1. THE BUG: AKS cluster itself FAILED, children skipped (NOT_PROCESSED). Multi-level chain.
    //    Expect: parent shows "Failed", NOT "Needs attention".
    [
      's1-aks-cluster-failed',
      aks(ResourceStatus.FAILED, [
        nodePool(ResourceStatus.NOT_PROCESSED, [
          ns(ResourceStatus.NOT_PROCESSED, [
            deploy(ResourceStatus.NOT_PROCESSED),
            deploy(ResourceStatus.NOT_PROCESSED),
          ]),
        ]),
      ]),
    ],
    // 2. Healthy EKS parent with one failed child. Expect: "Needs attention" + issue icon.
    [
      's2-eks-healthy-failed-child',
      eks(ResourceStatus.SUCCEEDED, [
        ns(ResourceStatus.SUCCEEDED, [deploy(ResourceStatus.FAILED), deploy(ResourceStatus.SUCCEEDED)]),
      ]),
    ],
    // 3. In-progress EKS parent with a failed + pending child. Expect: "Needs attention".
    [
      's3-eks-inprogress-failed-child',
      eks(ResourceStatus.PROCESSING, [
        ns(ResourceStatus.PROCESSING, [deploy(ResourceStatus.FAILED), deploy(ResourceStatus.PENDING)]),
      ]),
    ],
    // 4a. Parent itself NOT_PROCESSED (a trigger state, not just FAILED). Expect: own "Not Processed" pill.
    [
      's4a-aks-not-processed',
      aks(ResourceStatus.NOT_PROCESSED, [ns(ResourceStatus.NOT_PROCESSED, [deploy(ResourceStatus.NOT_PROCESSED)])]),
    ],
    // 4b. Parent UNSATISFIED_DEPENDENCY — a RECOVERY-only trigger. Expect: own pill in Recovery,
    //     but "Needs attention" in Backup (where it isn't a trigger). Side-by-side divergence.
    [
      's4b-eks-unsatisfied-dep',
      eks(ResourceStatus.UNSATISFIED_DEPENDENCY, [ns(ResourceStatus.SUCCEEDED, [deploy(ResourceStatus.FAILED)])]),
    ],
    // 5. Parent NO_STATUS (not a trigger) with a failed child. Expect: "Needs attention" in both.
    [
      's5-eks-no-status',
      eks(ResourceStatus.NO_STATUS, [ns(ResourceStatus.SUCCEEDED, [deploy(ResourceStatus.FAILED)])]),
    ],
    // 6. Parent SCAN_INFECTED with a SCAN_FAILED child. Expect: own pill in both (trigger in each set).
    [
      's6-aks-scan-infected',
      aks(ResourceStatus.SCAN_INFECTED, [ns(ResourceStatus.SUCCEEDED, [deploy(ResourceStatus.SCAN_FAILED)])]),
    ],
    // 7. RDS Aurora cluster, all children healthy. Expect: own Success pill, no rollup, no issue icon.
    [
      's7-aurora-all-healthy',
      {
        type: ResourceType.rdsDbCluster,
        status: ResourceStatus.SUCCEEDED,
        children: [dbInstance(ResourceStatus.SUCCEEDED), dbInstance(ResourceStatus.SUCCEEDED)],
      },
    ],
    // 8. Settled EKS parent with in-flight children. Expect: parent promoted to "Restoring"
    //    (_displayStatus), no rollup since PROCESSING/PENDING aren't trigger states.
    [
      's8-eks-settled-children-inflight',
      eks(ResourceStatus.SUCCEEDED, [
        ns(ResourceStatus.PROCESSING, [deploy(ResourceStatus.PROCESSING), deploy(ResourceStatus.PENDING)]),
      ]),
    ],
    // Extra: Auto Scaling Group → EC2 Instances (common non-K8s group), one instance failed.
    //        Expect: "Needs attention" on the healthy ASG.
    [
      's9-asg-failed-instance',
      {
        type: ResourceType.autoScalingGroup,
        status: ResourceStatus.SUCCEEDED,
        children: [ec2(ResourceStatus.FAILED), ec2(ResourceStatus.SUCCEEDED)],
      },
    ],
  ]

  const out: ViewableApplicationResource[] = []
  for (const [prefix, tree] of scenarios) {
    flattenScenario(tree, prefix, null, out)
  }
  return out
}

// -------------------------------------------------------------------------
// Mock Application factory for the test page modals showcase
// -------------------------------------------------------------------------

function buildMockRecoveryPoints(includeIgw: boolean, includeSg: boolean, includeVnet: boolean): RecoveryPointDto[] {
  const now = Date.now()
  const hour = 60 * 60 * 1000
  const resourceTypeStats: Array<{ resourceType: ResourceType }> = []
  if (includeIgw) resourceTypeStats.push({ resourceType: ResourceType.ec2InternetGateway })
  if (includeSg) resourceTypeStats.push({ resourceType: ResourceType.ec2SecurityGroup })
  if (includeVnet) resourceTypeStats.push({ resourceType: ResourceType.azureVirtualNetwork })

  // 8 RPs across the last 24h, with 2 protected (locked) so the
  // "Restrict to indefinitely-retained" toggle has rows to filter to.
  return [0, 1, 3, 6, 9, 12, 18, 24].map((hoursAgo, idx) => ({
    recoveryPointId: `mock-rp-${idx}`,
    timestamp: new Date(now - hoursAgo * hour),
    protected: idx === 0 || idx === 4,
    stats: { resourceTypeStats } as any,
  })) as RecoveryPointDto[]
}

export interface MockApplicationOptions {
  applicationId?: string
  name?: string
  type?: ApplicationType
  syncPhase?: SyncPhaseDto
  rpo?: number
  networkSandboxEnabled?: boolean
  advancedNetworkSandbox?: boolean
  withIgw?: boolean
  withSg?: boolean
  withVnet?: boolean
  // Region overrides. The launch dialogs scope their candidate app list to
  // apps sharing the anchor's source+target region, so giving each sandbox
  // scenario a unique region pair isolates it — otherwise a FAILOVER_TEST app
  // in the shared region would flip sandboxFrozen on unrelated scenarios.
  sourceRegion?: string
  targetRegion?: string
  // Resources returned by getRestoreResources(). The unquarantine dialog reads
  // these and shows the ones in a quarantine status; empty by default.
  restoreResources?: ViewableApplicationResource[]
  // Restore-completion state. When unset, a FAILOVER_TEST app reads as still
  // restoring ("Awaiting restore…"), which the conclude-test dialog buckets as
  // cancel-bound. Set these to make the app read as "restore complete" so the
  // full conclude flow (test outcome + generate report) is reachable.
  lastRestoreComplete?: Date
  lastRestoreSuccess?: Date
}

/**
 * Returns an `Application`-shaped object suitable for mounting any of the
 * launch / conclude / failback / unquarantine dialogs without a real backend.
 * Cast as `Application` — only the fields read by those dialogs are populated.
 */
export function buildMockApplication(opts: MockApplicationOptions = {}): Application {
  const {
    applicationId = `mock-app-${Math.random().toString(36).slice(2, 8)}`,
    name = 'Mock application',
    type = ApplicationType.AWS,
    syncPhase = SyncPhaseDto.STANDBY,
    rpo = 60,
    networkSandboxEnabled = false,
    advancedNetworkSandbox = false,
    withIgw = type === ApplicationType.AWS,
    withSg = type === ApplicationType.AWS,
    withVnet = type === ApplicationType.AZURE,
    sourceRegion = type === ApplicationType.AZURE ? 'eastus' : 'us-east-1',
    targetRegion = type === ApplicationType.AZURE ? 'westus' : 'us-west-2',
    restoreResources = [],
    lastRestoreComplete,
    lastRestoreSuccess,
  } = opts

  const recoveryPoints = buildMockRecoveryPoints(withIgw, withSg, withVnet)
  const cloud = type === ApplicationType.AZURE ? 'azure' : 'aws'

  const app: Partial<Application> & Record<string, unknown> = {
    applicationId,
    name,
    type,
    syncPhase,
    rpo,
    accountId: 'mock-account-id',
    sourceRegion,
    targetRegion,
    sourceAwsAccountId: '111111111111',
    targetAwsAccountId: '222222222222',
    sourceEndpointUri: `${cloud}:111111111111:${sourceRegion}`,
    targetEndpointUri: `${cloud}:222222222222:${targetRegion}`,

    // Recovery points + the loader the dialog subscribes to in ngOnInit.
    recoveryPoints,
    refreshRecoveryPoints$: () => of(null),

    // Sandbox-related getters are read directly off the application.
    networkSandboxEnabled,
    networkSandboxWhitelist: [],
    networkSandboxAllowedDomains: [],
    networkSandboxAllowedServices: [],
    advancedNetworkSandboxEnabled: advancedNetworkSandbox,

    // Methods occasionally hit by issue components nested in dialogs.
    getSyncPairResourceConfig$: () => of(null),
    manualResourceTemplateUrl$: of(''),

    // Restore-scope resources the unquarantine dialog reads (it filters these
    // to the ones in a quarantine status). Empty unless the scenario seeds a
    // set. updateRestoreResourceStatus is stubbed so the dialog's optimistic
    // post-unquarantine update doesn't crash.
    // Cast: mkRecoveryResource yields ViewableApplicationResource POJOs, which
    // aren't structurally assignable to the ApplicationResource class the real
    // getRestoreResources returns. The dialog only reads plain fields off them.
    getRestoreResources: (_filter?: string) => restoreResources as any,
    updateRestoreResourceStatus: (_status: any, _arns: any) => {},

    currentlyRestoring: false,
    lastRestoreComplete,
    lastRestoreSuccess,
    identityProviderId: null,
  }

  return app as unknown as Application
}

// Quarantined resources for the unquarantine dialog. Only EC2 instances can be
// unquarantined, so all rows are ec2Instance. Mix of clean (QUARANTINED /
// SCAN_CLEAN) and unclean (SCAN_INFECTED / SCAN_FAILED) statuses so the
// "type I understand" acknowledgement flow is reachable when an unclean row is
// selected.
export function buildMockQuarantinedResources(): ViewableApplicationResource[] {
  const mk = (name: string, status: ResourceStatus) =>
    mkRecoveryResource({ name, type: ResourceType.ec2Instance, status })
  return [
    mk('web-server-01', ResourceStatus.QUARANTINED),
    mk('web-server-02', ResourceStatus.QUARANTINED),
    mk('app-server-01', ResourceStatus.SCAN_CLEAN),
    mk('app-server-02', ResourceStatus.QUARANTINED),
    mk('db-primary', ResourceStatus.SCAN_INFECTED),
    mk('cache-node-01', ResourceStatus.SCAN_FAILED),
  ]
}

// Azure quarantined resources for the unquarantine dialog (ARP-7636). Azure VMs
// can now be unquarantined, so the primary rows are azureVirtualMachine with the
// same clean/unclean status mix as the EC2 set. One scale set is included to
// mirror production: the dialog lists every quarantine-status resource, but only
// ec2Instance + azureVirtualMachine map to a resourceConfigType, so the scale set
// shows in the list yet is silently excluded from the unquarantine config write.
export function buildMockAzureQuarantinedResources(): ViewableApplicationResource[] {
  const azureId = (name: string, type: ResourceType) =>
    `/subscriptions/00000000-0000-0000-0000-000000000000/resourceGroups/mock-rg/providers/${type}/${name}`
  const vm = (name: string, status: ResourceStatus) =>
    mkRecoveryResource({
      name,
      type: ResourceType.azureVirtualMachine,
      status,
      arn: azureId(name, ResourceType.azureVirtualMachine),
    })
  return [
    vm('web-vm-01', ResourceStatus.QUARANTINED),
    vm('web-vm-02', ResourceStatus.QUARANTINED),
    vm('app-vm-01', ResourceStatus.SCAN_CLEAN),
    vm('db-vm-01', ResourceStatus.SCAN_INFECTED),
    vm('cache-vm-01', ResourceStatus.SCAN_FAILED),
    mkRecoveryResource({
      name: 'worker-scaleset',
      type: ResourceType.azureVirtualMachineScaleSet,
      status: ResourceStatus.QUARANTINED,
      arn: azureId('worker-scaleset', ResourceType.azureVirtualMachineScaleSet),
    }),
  ]
}

// -------------------------------------------------------------------------
// Network sandbox scenario matrix (ARP-7142)
//
// The sandbox UI in launch-test-dialog and application-test-config-dialog
// branches on three customer-configuration axes: cloud (AWS/Azure), the
// resource types present in the recovery point (Internet Gateway / Security
// Group for AWS, Virtual Network for Azure), and account capability flags.
// Each scenario below fixes those axes so the test page can open the dialog
// in one specific rendered state. `disabledCapabilities` lists capabilities
// to turn OFF for the scenario (all are ON by default in the modals showcase);
// `siblingFailoverTest` seeds that many extra in-test apps sharing the anchor's
// region, which drives sandboxFrozen (launch-test) or the "also applies to"
// notice (sandbox settings).
// -------------------------------------------------------------------------

export interface SandboxScenario {
  key: string
  label: string
  // optgroup heading in the scenario picker
  group: string
  appOpts: MockApplicationOptions
  disabledCapabilities?: string[]
  siblingFailoverTest?: number
}

// launch-test-dialog: anchor is a STANDBY app (the dialog launches a test from
// standby). Covers the full radio/availability/frozen/capability matrix.
export const LAUNCH_TEST_SANDBOX_SCENARIOS: SandboxScenario[] = [
  {
    key: 'aws-off',
    label: 'AWS — sandbox off',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: false, withIgw: true, withSg: true },
  },
  {
    key: 'aws-basic',
    label: 'AWS — Security Group (basic)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: false,
      withIgw: true,
      withSg: true,
    },
  },
  {
    key: 'aws-advanced',
    label: 'AWS — Network Firewall (advanced)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: true,
      withIgw: true,
      withSg: true,
    },
  },
  {
    key: 'aws-advanced-no-igw',
    label: 'AWS — advanced unavailable (no Internet Gateway)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: true,
      withIgw: false,
      withSg: true,
    },
  },
  {
    key: 'aws-basic-no-sg',
    label: 'AWS — basic unavailable (no Security Group)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: false,
      withIgw: true,
      withSg: false,
    },
  },
  {
    key: 'aws-unavailable',
    label: 'AWS — sandbox unavailable (no SG or IGW)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      withIgw: false,
      withSg: false,
    },
  },
  {
    key: 'aws-frozen',
    label: 'AWS — frozen (sibling app in test)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      withIgw: true,
      withSg: true,
    },
    siblingFailoverTest: 1,
  },
  {
    key: 'aws-whitelist-off',
    label: 'AWS — allow-list capability off',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      withIgw: true,
      withSg: true,
    },
    disabledCapabilities: ['networkSandboxWhitelist'],
  },
  {
    key: 'aws-advanced-cap-off',
    label: 'AWS — advanced capability off',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: false,
      withIgw: true,
      withSg: true,
    },
    disabledCapabilities: ['advancedNetworkSandbox'],
  },
  {
    key: 'azure-on',
    label: 'Azure — sandbox on',
    group: 'Azure',
    appOpts: {
      type: ApplicationType.AZURE,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: true,
      withVnet: true,
    },
  },
  {
    key: 'azure-off',
    label: 'Azure — sandbox off',
    group: 'Azure',
    appOpts: { type: ApplicationType.AZURE, networkSandboxEnabled: false, withVnet: true },
  },
  {
    key: 'azure-unavailable',
    label: 'Azure — sandbox unavailable (no Virtual Network)',
    group: 'Azure',
    appOpts: {
      type: ApplicationType.AZURE,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: true,
      withVnet: false,
    },
  },
]

// application-test-config-dialog: anchor is a FAILOVER_TEST app (settings are
// edited while a test is running). No radios or RP-availability gating — the
// view is driven by the app's advanced flag, cloud, and the sandbox capability.
export const SANDBOX_CONFIG_SCENARIOS: SandboxScenario[] = [
  {
    key: 'aws-basic',
    label: 'AWS — Security Group (basic)',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: true, advancedNetworkSandbox: false },
  },
  {
    key: 'aws-advanced',
    label: 'AWS — Network Firewall (advanced)',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: true, advancedNetworkSandbox: true },
  },
  {
    key: 'aws-multi-app',
    label: 'AWS — advanced, applies to sibling apps',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: true, advancedNetworkSandbox: true },
    siblingFailoverTest: 2,
  },
  {
    key: 'aws-cap-off',
    label: 'AWS — sandbox capability off',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: true, advancedNetworkSandbox: false },
    disabledCapabilities: ['networkSandbox'],
  },
  {
    key: 'azure',
    label: 'Azure — advanced',
    group: 'Azure',
    appOpts: { type: ApplicationType.AZURE, networkSandboxEnabled: true, advancedNetworkSandbox: true },
  },
]

// Mock SyncPairAccessDto for the "Let's get you updated" (update-access)
// dialog. Defaults to "out of date" — sourceVersion < latest, so the dialog
// shows the upgrade affordance instead of an all-good empty state.
export function buildMockSyncPairAccess(): SyncPairAccessDto {
  return {
    sourceAccess: true,
    sourceConfigValid: true,
    sourceVersion: 12,
    sourceIsLatest: false,
    sourceCloudFormationAccessStackName: 'arpio-source-access',
    targetAccess: true,
    targetConfigValid: true,
    targetVersion: 12,
    targetIsLatest: false,
    targetCloudFormationAccessStackName: 'arpio-target-access',
    failbackAccess: true,
    failbackConfigValid: true,
    failbackVersion: 12,
    failbackIsLatest: false,
    failbackCloudFormationAccessStackName: 'arpio-failback-access',
  }
}

// Mock AccessTemplatesDto so the dialog finishes loading and renders the
// inner aws-account-access-view rows with stack templates instead of the
// gray skeleton placeholders.
export function buildMockAccessTemplates(): AccessTemplatesDto {
  return {
    sourceTemplateS3Url: 'https://example.s3.amazonaws.com/source.yml',
    targetTemplateS3Url: 'https://example.s3.amazonaws.com/target.yml',
    failbackTemplateS3Url: 'https://example.s3.amazonaws.com/failback.yml',
  }
}

// ──────────────────────────────────────────────────────────────────────────
//  StackSets regeneration-job fixtures
// ──────────────────────────────────────────────────────────────────────────

// Drives the patched apiService.getJob to return a deterministic status sequence
// so reviewers can step through every gating outcome the production components handle.
export type StacksetsJobScenarioKey =
  | 'none'
  | 'instant_done'
  | 'running_then_done'
  | 'running_then_failed'
  | 'running_timeout'
  | 'http_error'
  | 'skipped'

export interface StacksetsJobScenario {
  key: StacksetsJobScenarioKey
  label: string
  description: string
}

export const STACKSETS_JOB_SCENARIOS: ReadonlyArray<StacksetsJobScenario> = [
  {
    key: 'none',
    label: 'No job',
    description: 'Backend returned no jobId — components render normally with no gating.',
  },
  {
    key: 'instant_done',
    label: 'Instant done',
    description: 'Cache hit — first poll returns done. Spinner may flash briefly.',
  },
  {
    key: 'running_then_done',
    label: 'Running → done',
    description: 'Polls return running for ~10s, then done. Typical happy path.',
  },
  {
    key: 'running_then_failed',
    label: 'Failed',
    description: 'First poll returns failed. UI unblocks immediately and a "Template preparation failed" toast fires.',
  },
  {
    key: 'running_timeout',
    label: 'Simulated timeout',
    description:
      'Polls keep returning running. Client gives up after 1s (production: 120s), unblocks, and toasts "Templates may still be generating".',
  },
  {
    key: 'http_error',
    label: 'HTTP error',
    description: 'getJob throws — handleArpioError fires (toast or 401 redirect).',
  },
  {
    key: 'skipped',
    label: 'Skipped',
    description: 'Terminal status of skipped — degenerate case, treated as success.',
  },
]

// Per-scenario static job IDs so flipping scenarios is observable to ngOnChanges
// in the showcased components (the input value must actually change to retrigger polling).
export const STACKSETS_JOB_IDS: Record<StacksetsJobScenarioKey, string | null> = {
  none: null,
  instant_done: 'mock-job-instant',
  running_then_done: 'mock-job-running-done',
  running_then_failed: 'mock-job-running-failed',
  running_timeout: 'mock-job-timeout',
  http_error: 'mock-job-http-error',
  skipped: 'mock-job-skipped',
}

export function buildMockJobDto(jobId: string, status: JobStatus, accountId = 'test-account-id'): JobDto {
  return {
    jobId,
    accountId,
    status,
    trigger: 'automatic',
    startTime: new Date().toISOString(),
    jobType: 'stackSetTemplateRegeneration',
  }
}

// ──────────────────────────────────────────────────────────────────────────
//  StackSets UI scenario presets
// ──────────────────────────────────────────────────────────────────────────
//
// Each preset seeds the @Input set on either <stacksets-instructions> or
// <stacksets-update-instructions> to produce one specific UI variant. The
// regen-job axis (STACKSETS_JOB_SCENARIOS above) is independent.

export type StacksetsFlow = 'setup' | 'update'

export interface StacksetsScenarioInputs {
  // setup-flow inputs
  migrationMode?: boolean
  sourceManagedByStackSet?: boolean
  targetManagedByStackSet?: boolean
  sourceStackName?: string
  targetStackName?: string
  testingSource?: boolean
  testingTarget?: boolean
  sourceError?: boolean
  targetError?: boolean
  sourceTestCount?: number
  targetTestCount?: number
  // update-flow inputs (also reuses sourceTestCount / targetTestCount above)
  sourceAccessState?: AccessState
  targetAccessState?: AccessState
}

export interface StacksetsScenarioPreset {
  key: string
  flow: StacksetsFlow
  group: string
  label: string
  description: string
  inputs: StacksetsScenarioInputs
}

export const STACKSETS_SCENARIO_PRESETS: ReadonlyArray<StacksetsScenarioPreset> = [
  // ── Setup flow — Create flow ─────────────────────────────────────────
  {
    key: 'setup-create-fresh',
    flow: 'setup',
    group: 'Create flow',
    label: 'Fresh — neither managed',
    description: 'Brand-new app. Both source and target need StackSets onboarding. No CFN stacks present.',
    inputs: { migrationMode: false, sourceManagedByStackSet: false, targetManagedByStackSet: false },
  },
  {
    key: 'setup-create-cfn-source',
    flow: 'setup',
    group: 'Create flow',
    label: 'CFN conflict — source',
    description: 'Source has a legacy CFN access stack but is not yet managed by StackSets — top-level warning fires.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceStackName: 'arpio-source-access-app',
    },
  },
  {
    key: 'setup-create-cfn-target',
    flow: 'setup',
    group: 'Create flow',
    label: 'CFN conflict — target',
    description: 'Target has a legacy CFN access stack but is not yet managed by StackSets.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      targetStackName: 'arpio-target-access-app',
    },
  },
  {
    key: 'setup-create-cfn-both',
    flow: 'setup',
    group: 'Create flow',
    label: 'CFN conflict — both',
    description: 'Both source and target have legacy CFN access stacks — plural-account messaging.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceStackName: 'arpio-source-access-app',
      targetStackName: 'arpio-target-access-app',
    },
  },
  {
    key: 'setup-create-source-configured',
    flow: 'setup',
    group: 'Create flow',
    label: 'Source configured, target pending',
    description: 'Source pair is managed; target still needs its StackSets deploy.',
    inputs: { migrationMode: false, sourceManagedByStackSet: true, targetManagedByStackSet: false },
  },
  {
    key: 'setup-create-target-configured',
    flow: 'setup',
    group: 'Create flow',
    label: 'Target configured, source pending',
    description: 'Target pair is managed; source still needs its StackSets deploy.',
    inputs: { migrationMode: false, sourceManagedByStackSet: false, targetManagedByStackSet: true },
  },
  {
    key: 'setup-create-both-configured',
    flow: 'setup',
    group: 'Create flow',
    label: 'Both configured',
    description: 'Setup is complete in create flow — both pairs show "Access configured via StackSets".',
    inputs: { migrationMode: false, sourceManagedByStackSet: true, targetManagedByStackSet: true },
  },
  // ── Setup flow — Test access states ─────────────────────────────────
  {
    key: 'setup-test-source-progress',
    flow: 'setup',
    group: 'Test access states',
    label: 'Source — testing in progress',
    description: 'Source TEST ACCESS button shows the spinner + TESTING… disabled state.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      testingSource: true,
    },
  },
  {
    key: 'setup-test-source-error-1',
    flow: 'setup',
    group: 'Test access states',
    label: 'Source — error (1 retry)',
    description: 'After one failed test, the "It\'s still not working." retry message appears above the button.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceError: true,
      sourceTestCount: 1,
    },
  },
  {
    key: 'setup-test-source-error-2',
    flow: 'setup',
    group: 'Test access states',
    label: 'Source — error (2 retries)',
    description: 'After two failed tests, the alternate retry message ("Hmm... It\'s not working yet.") shows.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceError: true,
      sourceTestCount: 2,
    },
  },
  {
    key: 'setup-test-target-progress',
    flow: 'setup',
    group: 'Test access states',
    label: 'Target — testing in progress',
    description: 'Target TEST ACCESS button shows the spinner + TESTING… disabled state.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      testingTarget: true,
    },
  },
  // ── Setup flow — Migration ───────────────────────────────────────────
  {
    key: 'setup-migration-pre',
    flow: 'setup',
    group: 'Migration flow',
    label: 'Migration — starting',
    description:
      'Migration mode with both source and target still on CFN — the migration intent intro shows, no cleanup yet.',
    inputs: {
      migrationMode: true,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceStackName: 'arpio-source-access-app',
      targetStackName: 'arpio-target-access-app',
    },
  },
  {
    key: 'setup-migration-source-done',
    flow: 'setup',
    group: 'Migration flow',
    label: 'Migration — source migrated',
    description: 'Source flipped to StackSets but target still on CFN — partial migration state.',
    inputs: {
      migrationMode: true,
      sourceManagedByStackSet: true,
      targetManagedByStackSet: false,
      targetStackName: 'arpio-target-access-app',
    },
  },
  {
    key: 'setup-migration-complete',
    flow: 'setup',
    group: 'Migration flow',
    label: 'Migration — complete',
    description: 'Both pairs migrated — Steps 1-3 hide and the success banner + delete-stack cleanup commands show.',
    inputs: {
      migrationMode: true,
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceStackName: 'arpio-source-access-app',
      targetStackName: 'arpio-target-access-app',
    },
  },
  // ── Update flow — Setup mode (no StackSets configured yet) ───────────
  {
    key: 'update-setup-mode-fresh',
    flow: 'update',
    group: 'Update flow — setup mode',
    label: 'Setup mode — fresh',
    description:
      'Update component in setup mode (neither managed). Header reads "Set up StackSets"; commands say "Create".',
    inputs: { sourceManagedByStackSet: false, targetManagedByStackSet: false },
  },
  // ── Update flow — Update mode ────────────────────────────────────────
  {
    key: 'update-mode-fresh',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Update mode — fresh',
    description: 'Both managed, neither tested yet. Header reads "Update StackSets"; commands say "Update".',
    inputs: { sourceManagedByStackSet: true, targetManagedByStackSet: true },
  },
  {
    key: 'update-mode-source-success',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Source up to date',
    description: 'Primary access shows the green "up to date" notice; recovery still needs verification.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'update-mode-target-success',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Target up to date',
    description: 'Recovery access shows the green "up to date" notice; primary still needs verification.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      targetAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'update-mode-both-success',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Both up to date',
    description: 'Both pairs show "Access is up to date" — success terminal state for the update flow.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.SUCCESS,
      targetAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'update-mode-source-testing',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Source — testing',
    description: 'Primary TEST ACCESS button shows TESTING… spinner.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.TESTING,
    },
  },
  {
    key: 'update-mode-source-retry-1',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Source — retry (1)',
    description: 'After one failed test, the "It\'s still not working." retry message renders above the button.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.FAILED,
      sourceTestCount: 1,
    },
  },
  {
    key: 'update-mode-source-retry-2',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Source — retry (2)',
    description: 'After two failed tests, the alternate retry message renders.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.FAILED,
      sourceTestCount: 2,
    },
  },
]

// ──────────────────────────────────────────────────────────────────────────
//  RPO chart fixtures
// ──────────────────────────────────────────────────────────────────────────

// Days of staleness covered by the showcase (1d through 15d). The .5
// offset on the gap-end places the last data point clearly inside the
// "N days ago" gap rather than on the boundary.
const STALE_DAYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const
type StaleDayKey = `stale${(typeof STALE_DAYS)[number]}d`

export type ChartScenarioKey =
  | 'healthy'
  | 'single-point'
  | 'two-points-fresh'
  | 'all-failed'
  | 'long-history'
  | 'real-time'
  | 'burst-then-quiet'
  | 'boundary-7d'
  | 'empty'
  | 'loading'
  | StaleDayKey

export interface ChartScenario {
  key: ChartScenarioKey
  label: string
  description: string
}

export const CHART_SCENARIOS: ReadonlyArray<ChartScenario> = [
  { key: 'healthy', label: 'Healthy', description: 'Recent backups every 5 min, fresh as of now.' },
  {
    key: 'single-point',
    label: 'Single point',
    description: 'Exactly one successful backup ~1 hour ago. Exposes the length−1 iteration in the data builder.',
  },
  {
    key: 'two-points-fresh',
    label: 'Two points',
    description: 'Two consecutive backups in the last 10 minutes — minimum viable rendering.',
  },
  {
    key: 'all-failed',
    label: 'All failed',
    description: 'Five recent attempts, none with createSuccess — successful filter is empty.',
  },
  {
    key: 'long-history',
    label: 'Long history (60d)',
    description: '60 days of dense history, last point ~10 min ago. Tests pan/zoom beyond 2W.',
  },
  {
    key: 'real-time',
    label: 'Real-time (rpo=0)',
    description: 'rpo=0 with replication-latency samples instead of snapshot durations.',
  },
  {
    key: 'burst-then-quiet',
    label: 'Burst then quiet',
    description: 'Dense 12-hour burst 8 days ago, then total silence — clustered shape.',
  },
  {
    key: 'boundary-7d',
    label: 'Boundary — exactly 7d',
    description: 'Last point lands exactly 7 days ago. Tests the 1W threshold edge.',
  },
  ...STALE_DAYS.map<ChartScenario>((d) => ({
    key: `stale${d}d` as StaleDayKey,
    label: `No data — ${d} day${d === 1 ? '' : 's'}`,
    description: `Last successful backup ~${d}.5 days ago.`,
  })),
  { key: 'empty', label: 'No data ever', description: 'Backups loaded but the array is empty.' },
  { key: 'loading', label: 'Loading', description: 'recoveryPointAttempts is still null.' },
]

/**
 * RPO target (in seconds) the chart should show as a horizontal reference
 * line for a given scenario. Most scenarios use 5 min (a typical production
 * value); real-time uses 0 to flip the chart into replication-latency mode.
 */
export function getMockChartRpo(scenario: ChartScenarioKey): number {
  return scenario === 'real-time' ? 0 : 300
}

const FIVE_MIN_MS = 5 * 60 * 1000
const ONE_DAY_MS = 24 * 60 * 60 * 1000

/**
 * Build a series of RecoveryPointAttemptDto from `historyEndDaysAgo` ago up
 * to `lastAttemptDaysAgo` ago, one every ~5 minutes. Snapshot duration jitter
 * is deterministic (sin-based) so each render of the same scenario looks
 * identical.
 */
function buildAttempts(historyEndDaysAgo: number, lastAttemptDaysAgo: number): RecoveryPointAttemptDto[] {
  const now = Date.now()
  const startMs = now - historyEndDaysAgo * ONE_DAY_MS
  const endMs = now - lastAttemptDaysAgo * ONE_DAY_MS
  const attempts: RecoveryPointAttemptDto[] = []
  let i = 0
  for (let t = startMs; t <= endMs; t += FIVE_MIN_MS) {
    // Deterministic jitter — duration drifts between ~30s and ~6min so the
    // line has visible undulation. Occasional spikes simulate slow snapshots.
    const base = 60 + 90 * (Math.sin(i * 0.13) + 1) // 60–240s
    const spike = i % 47 === 0 ? 200 : 0 // ~every 47th sample, push a spike
    const durationSec = Math.round(base + spike)
    const timestamp = new Date(t)
    const createSuccess = new Date(t + durationSec * 1000)
    attempts.push({
      timestamp,
      createSuccess,
      recoveryPointId: `mock-rp-${i}`,
      maxReplicationLatency: null,
    } as RecoveryPointAttemptDto)
    i++
  }
  return attempts
}

/**
 * Resolve a chart scenario key to the input the rpo-chart component expects.
 * `null` is the "loading" sentinel — the chart doesn't render. `[]` is
 * "loaded but empty" — the chart renders with the no-data overlay.
 *
 * For `staleNd` scenarios: history starts max(N + 7, 14) days back so there
 * is always at least 7 days of context behind the gap, and the last point
 * lands at N + 0.5 days ago (clearly inside the "N days no data" window).
 */
export function buildMockChartData(scenario: ChartScenarioKey): RecoveryPointAttemptDto[] | null {
  if (scenario === 'loading') return null
  if (scenario === 'empty') return []
  if (scenario === 'healthy') {
    // 7 days history, last point ~2 min ago.
    return buildAttempts(7, 2 / (60 * 24))
  }
  if (scenario === 'single-point') {
    // Exactly one successful attempt 1 hour ago. computeBriefRpoData /
    // computeNotBriefRpoData both iterate length−1 → produce zero data
    // points from a single attempt. Renders as empty even though there
    // IS a successful backup. Worth surfacing.
    const t = Date.now() - 60 * 60 * 1000
    return [
      {
        timestamp: new Date(t),
        createSuccess: new Date(t + 60 * 1000),
        recoveryPointId: 'mock-single',
        maxReplicationLatency: null,
      } as RecoveryPointAttemptDto,
    ]
  }
  if (scenario === 'two-points-fresh') {
    // Two attempts 5 min apart, both within the last 10 minutes.
    const now = Date.now()
    const mkAttempt = (minutesAgo: number, durationSec: number, idx: number): RecoveryPointAttemptDto => {
      const t = now - minutesAgo * 60 * 1000
      return {
        timestamp: new Date(t),
        createSuccess: new Date(t + durationSec * 1000),
        recoveryPointId: `mock-pair-${idx}`,
        maxReplicationLatency: null,
      } as RecoveryPointAttemptDto
    }
    return [mkAttempt(10, 60, 0), mkAttempt(5, 75, 1)]
  }
  if (scenario === 'all-failed') {
    // Five attempts in the last hour, none with createSuccess. The
    // successfulRecoveryPointAttempts filter strips them all → empty
    // snapshotData → "No recent data" overlay.
    const now = Date.now()
    const attempts: RecoveryPointAttemptDto[] = []
    for (let i = 0; i < 5; i++) {
      const t = now - (i + 1) * 10 * 60 * 1000
      attempts.push({
        timestamp: new Date(t),
        createSuccess: null,
        recoveryPointId: `mock-failed-${i}`,
        maxReplicationLatency: null,
      } as RecoveryPointAttemptDto)
    }
    return attempts
  }
  if (scenario === 'long-history') {
    // 60 days of attempts, ending ~10 min ago.
    return buildAttempts(60, 10 / (60 * 24))
  }
  if (scenario === 'real-time') {
    // rpo=0 mode — chart switches to replication-latency rendering.
    // Generate attempts with non-null maxReplicationLatency every 5 min
    // for the last 4 hours. The chart's `showReplicationLatencies` flag
    // gates on rpo === 0 + at least one non-null replication value.
    const now = Date.now()
    const attempts: RecoveryPointAttemptDto[] = []
    let i = 0
    for (let t = now - 4 * 60 * 60 * 1000; t <= now - 2 * 60 * 1000; t += FIVE_MIN_MS) {
      // Latency drifts between ~3s and ~30s with deterministic variation.
      const latencySec = 3 + 12 * (Math.sin(i * 0.27) + 1)
      attempts.push({
        timestamp: new Date(t),
        createSuccess: new Date(t + 30 * 1000),
        recoveryPointId: `mock-rt-${i}`,
        maxReplicationLatency: Math.round(latencySec * 1000),
      } as RecoveryPointAttemptDto)
      i++
    }
    return attempts
  }
  if (scenario === 'burst-then-quiet') {
    // Dense 12-hour burst centered ~8 days ago, then nothing. The last
    // data point sits at ~7.5 days ago, inside the 2W window but outside
    // 1W. Visually striking shape: a tall narrow cluster on an otherwise
    // empty timeline.
    return buildAttempts(8.5, 7.5)
  }
  if (scenario === 'boundary-7d') {
    // Last point lands exactly 7d ago. Tests whether `hasDataIn(7d)`'s
    // `d.x >= now - 7d` boundary condition includes the point (1W picked)
    // or excludes it (escalates to 2W).
    return buildAttempts(14, 7)
  }
  // staleNd → match the digits between "stale" and "d" so a single
  // generator handles every day in STALE_DAYS without a hand-typed switch.
  const match = /^stale(\d+)d$/.exec(scenario)
  if (match) {
    const days = parseInt(match[1], 10)
    const historyEndDaysAgo = Math.max(days + 7, 14)
    const lastAttemptDaysAgo = days + 0.5
    return buildAttempts(historyEndDaysAgo, lastAttemptDaysAgo)
  }
  return null
}

// Mirrors the network-firewall events the customer sees in production: TCP/443
// egress that the sandbox blocked, mostly to package / cloud-vendor SNIs.
// Used by the test page to render the event log modal in its populated state.
export function buildMockFirewallEvents(): AwsEventLogEntry[] {
  const base = Date.now()
  const make = (offsetSec: number, partial: Partial<AwsEventLogEntry>): AwsEventLogEntry => ({
    timestamp: new Date(base - offsetSec * 1000),
    srcIp: '10.131.2.63',
    srcPort: 61782,
    destIp: '185.125.188.58',
    destPort: 443,
    proto: 'TCP',
    action: AwsEventLogAction.Blocked,
    firewallName: 'mock-app-fw',
    flowId: 0,
    host: null,
    sni: null,
    ...partial,
  })
  return [
    make(0, { sni: 'api.snapcraft.io' }),
    make(3, { srcIp: '10.131.8.100', srcPort: 10573, destIp: '98.82.159.50', sni: 'api.amazon.com' }),
    make(3, {
      srcIp: '10.131.8.100',
      srcPort: 58548,
      destIp: '98.82.159.50',
      sni: 'api.amazon.com',
      flowId: 1,
    }),
    make(9, { srcIp: '10.131.7.184', srcPort: 57947, destIp: '99.80.48.20', sni: 'api.amazon.com', flowId: 2 }),
    make(14, {
      srcIp: '10.131.8.100',
      srcPort: 58548,
      destIp: '98.82.159.50',
      sni: 'api.amazon.com',
      flowId: 3,
    }),
    make(14, {
      srcIp: '10.131.8.100',
      srcPort: 58548,
      destIp: '98.82.159.50',
      sni: 'api.amazon.com',
      flowId: 4,
    }),
    make(22, { sni: 'api.snapcraft.io', flowId: 5 }),
    make(31, {
      srcIp: '10.131.7.184',
      srcPort: 39214,
      destIp: '52.46.131.27',
      sni: 's3.us-east-1.amazonaws.com',
      flowId: 6,
    }),
    make(45, {
      srcIp: '10.131.2.63',
      srcPort: 49810,
      destIp: '140.82.114.4',
      sni: 'github.com',
      flowId: 7,
    }),
    make(58, {
      srcIp: '10.131.8.100',
      srcPort: 51220,
      destIp: '34.107.221.82',
      sni: 'pkg.go.dev',
      flowId: 8,
    }),
    make(72, {
      srcIp: '10.131.7.184',
      srcPort: 60441,
      destIp: '151.101.1.140',
      sni: 'pypi.org',
      action: AwsEventLogAction.Allowed,
      flowId: 9,
    }),
    make(95, {
      srcIp: '10.131.2.63',
      srcPort: 41108,
      destIp: '52.92.176.0',
      sni: 'sts.us-east-1.amazonaws.com',
      action: AwsEventLogAction.Allowed,
      flowId: 10,
    }),
  ]
}

// -------------------------------------------------------------------------
// Recovery / Failback screen scenarios (ARP-7143)
// -------------------------------------------------------------------------
//
// One entry per row in the screen-preview picker. Each scenario carries a
// fully-realized snapshot of every input the composed screen needs:
//   - the mock Application (sync phase + sandbox/quarantine flags)
//   - the rows the resource table renders
//   - the prebuilt status object the tab-status-bar template consumes
//   - precomputed operation-notice fields (so we don't have to duplicate the
//     getter logic that lives on ApplicationComponent).
//
// Production code is never modified — the test page composes the same child
// components (<app-resources-view>, the inlined statusBarTemplate, the
// operation-notice div) the real screen does and feeds them these snapshots.
//
// Operation-notice in production reads ApplicationComponent.currentRecoveryPointTimeString /
// networkSandboxStatus / domainAllowlistStatus / etc.  Those getters depend on
// `accountService.entitledForCapability('networkSandbox')`, `quarantineEnabled`,
// `networkSandboxWhitelistEnabled` — too much wiring to replicate accurately.
// The scenario precomputes the visible booleans + strings so the test page
// binds directly without re-deriving them.

export interface RecoveryScreenOperationNotice {
  recoveryPointTimeString: string
  showNetworkSandboxStatus: boolean
  networkSandboxStatus: 'enabled' | 'disabled'
  // Drives the "See most recent events" inline link in advanced sandbox mode.
  showSandboxEventsLink: boolean
  showRansomwareRecoveryStatus: boolean
  ransomwareRecoveryStatus: 'enabled' | 'disabled'
  // Empty string = no row (mirrors the *ngIf gate in application.component.html).
  domainAllowlistStatus: string
  whitelistStatus: string
}

// Visual-only flags driving the tab strip + action buttons surfaced above the
// composed screen. Mirrors the production gating in application.component.html
// tab-bar-actions + the `disabledActionReason` getter in application.component.ts.
// Kept as plain values so the test page doesn't have to invoke the real
// `entitledForCapability` / `canRecover` / `lastStagingSuccess` pipelines.
export interface RecoveryScreenTabBarFlags {
  // Mirrors ApplicationComponent.disabledActionReason. Per-phase buttons always
  // render; when this is non-null every action is disabled and shows this string
  // as its tooltip (a failback/restore in flight, or a conclude-to-standby
  // wind-down). null = actions enabled. Replaces the old spinner-collapse model.
  disabledActionReason: string | null
  showSandboxSettings: boolean
  // STANDBY-phase actions
  showTestButton: boolean
  showRecoverButton: boolean
  // STANDBY disabled when no successful backup has landed yet.
  testRecoverDisabled: boolean
  // FAILOVER-phase actions
  showUnquarantine: boolean
  showFailback: boolean
  // FAILBACK disabled until backend reports failback resources.
  failbackDisabled: boolean
  showConcludeRecovery: boolean
  // FAILOVER_TEST-phase action
  showConcludeTest: boolean
  // Toggles label between CONCLUDE TEST and CANCEL TEST (early stop).
  isEarlyStopTest: boolean
}

export interface RecoveryScreenScenario {
  key: string
  label: string
  description: string
  // Which sub-tab the scenario renders under. Drives the Recovery/Failback
  // chip-button highlight and `tabType` fed into app-resources-view.
  tab: 'recovery' | 'failback'
  // optgroup label in the dropdown — phase or context bucket.
  group: 'STANDBY' | 'FAILOVER' | 'FAILOVER_TEST' | 'CLEANUP' | 'FAILBACK'
  syncPhase: SyncPhaseDto
  application: Application
  resources: ViewableApplicationResource[]
  // RecoveryEnvironmentStatus | FailbackStatus depending on tab. Tab-status-bar
  // template consumes both shapes interchangeably.
  status: RecoveryEnvironmentStatus | FailbackStatus
  // null = render no notice (e.g. STANDBY recovery — matches the
  // `*ngIf="activeTab === Tab.RECOVERY_ENVIRONMENT && !isStandby"` gate).
  // For Failback, null mirrors `failbackOperationNoticeText` returning null.
  operationNotice: RecoveryScreenOperationNotice | null
  failbackOperationNoticeText: string | null
  tabBarFlags: RecoveryScreenTabBarFlags
}

interface MockResourceOpts {
  name: string
  type: ResourceType
  status: ResourceStatus
  // Recovery-context defaults: source resource name visible, target name
  // mirrors source. Override targetName='' to render a pilot-light row
  // (source visible, target column empty). Override sourceName='' to render
  // an orphaned-target row.
  sourceName?: string
  targetName?: string
  progress?: number
  message?: string
  substatus?: string
  parentArn?: string
  arn?: string
}

// ViewableApplicationResource POJO factory mirroring the construction-time
// substatus rules from ApplicationResource: pilot light gets the
// "Pilot-light resource…" hint, orphaned target gets "Not in the current
// recovery point", PENDING with confirming-substatus, NOT_PROCESSED with
// "Upstream dependency failed". Substatus is otherwise derived from
// computeResourceSubstatus's rules — we precompute it here rather than at
// runtime because the POJO doesn't invoke that helper.
function mkRecoveryResource(opts: MockResourceOpts): ViewableApplicationResource {
  const {
    name,
    type,
    status,
    sourceName = name,
    targetName = name,
    progress = 0,
    message = null,
    substatus,
    parentArn,
    arn,
  } = opts

  const computedSubstatus = (() => {
    if (substatus !== undefined) return substatus
    // Pilot-light: source visible, target blank, NO_STATUS — matches isPilotLight().
    if (status === ResourceStatus.SUCCEEDED && sourceName && !targetName) {
      return 'Pilot-light resource — created on demand during recovery'
    }
    // Orphaned target: target visible, source blank, NO_STATUS.
    if (status === ResourceStatus.NO_STATUS && !sourceName && targetName) {
      return 'Not in the current recovery point'
    }
    switch (status) {
      case ResourceStatus.PROCESSING:
        return progress > 0 && progress < 1 ? `${Math.round(progress * 100)}%` : message || undefined
      case ResourceStatus.PENDING:
        return message || 'Waiting in queue...'
      case ResourceStatus.UNSATISFIED_DEPENDENCY:
        return message || 'Waiting for dependency'
      case ResourceStatus.NOT_PROCESSED:
        return 'Upstream dependency failed'
      case ResourceStatus.FAILED:
        return message || undefined
      default:
        return undefined
    }
  })()

  return {
    resourceType: type,
    resourceVariant: null,
    resourceArn: (arn ?? `arn:aws:mock:us-east-1:123456789012:${type}/${name}`) as Arn,
    sourceResourceName: sourceName,
    targetResourceName: targetName,
    resourceProperties: [],
    status,
    progress,
    message,
    substatus: computedSubstatus,
    issues: new Set(),
    displayResourceType: getResourceTypeDisplayString(type, null),
    resourceTypeDetails: getResourceTypeDetails({ resourceType: type }),
    parentArn,
  } as ViewableApplicationResource
}

// EKS parent + child pods. Children carry `parentArn` pointing at the
// cluster ARN so app-resources-view's buildDisplayRows groups them under
// the parent. Forcing one child into FAILED is what trips the
// needs-attention rollup pill on the cluster row.
function mkEksParentWithChildren(args: {
  clusterName: string
  childStatuses: ResourceStatus[]
}): ViewableApplicationResource[] {
  const parentArn = `arn:aws:eks:us-east-1:123456789012:cluster/${args.clusterName}`
  const parent = mkRecoveryResource({
    name: args.clusterName,
    type: ResourceType.eksCluster,
    status: ResourceStatus.SUCCEEDED,
    arn: parentArn,
  })
  const children = args.childStatuses.map((s, i) =>
    mkRecoveryResource({
      name: `${args.clusterName}-deploy-${i + 1}`,
      type: ResourceType.k8sResource,
      status: s,
      parentArn,
      arn: `${parentArn}/deployment-${i + 1}`,
    })
  )
  return [parent, ...children]
}

// Convenience: render the standard rendered "recovery point at HH:MM on Mon DD"
// suffix the real status strings use — keeps scenario labels consistent with
// production wording.
const RP_AT_3_14_MAY_8 = ' from recovery point at 03:14 PM on May 8'

// Hand-tuned resource sets per scenario. Realistic resource type mixes so
// the row count and filter pills look right (e.g., RDS + EC2 + S3 instead of
// the round-robin enum loop in buildStatusResources()).
function resourcesEmpty(): ViewableApplicationResource[] {
  return []
}

function resourcesAllPending(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({
      name: 'web-app-01',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.PROCESSING,
      progress: 0.4,
    }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.PENDING }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.PENDING }),
    // PENDING with the post-job-lag substatus — covers the "Confirming
    // status..." path that _computeStatus emits during the 30s window.
    mkRecoveryResource({
      name: 'reporting-fn',
      type: ResourceType.lambdaFunction,
      status: ResourceStatus.PENDING,
      substatus: 'Confirming status...',
    }),
    mkRecoveryResource({
      name: 'orders',
      type: ResourceType.dynamoDbTable,
      status: ResourceStatus.PROCESSING,
      progress: 0.8,
    }),
  ]
}

function resourcesSteadyState(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SUCCEEDED }),
    // Pilot light — source visible, target blank, status flipped to SUCCEEDED
    // at construction time. Substatus surfaces the hint via tooltip on the pill.
    mkRecoveryResource({
      name: 'jump-host',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.SUCCEEDED,
      targetName: '',
    }),
    // Orphaned target — target visible, source blank, NO_STATUS.
    mkRecoveryResource({
      name: 'old-test-bucket',
      type: ResourceType.s3Bucket,
      status: ResourceStatus.NO_STATUS,
      sourceName: '',
    }),
  ]
}

function resourcesMixedFailures(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.FAILED,
      message: 'DBSubnetGroup not found in target VPC.',
    }),
    mkRecoveryResource({
      name: 'db-replica',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.NOT_PROCESSED,
    }),
    mkRecoveryResource({
      name: 'reporting-fn',
      type: ResourceType.lambdaFunction,
      status: ResourceStatus.NOT_PROCESSED,
    }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBlocked(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({
      name: 'web-app-02',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.UNSATISFIED_DEPENDENCY,
      message: 'Waiting on db-primary',
    }),
    mkRecoveryResource({
      name: 'app-data',
      type: ResourceType.s3Bucket,
      status: ResourceStatus.UNSATISFIED_DEPENDENCY,
    }),
    mkRecoveryResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.PROCESSING,
      progress: 0.7,
    }),
  ]
}

function resourcesEksWithFailedChild(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    ...mkEksParentWithChildren({
      clusterName: 'orders-cluster',
      childStatuses: [
        ResourceStatus.SUCCEEDED,
        ResourceStatus.SUCCEEDED,
        ResourceStatus.PROCESSING,
        ResourceStatus.FAILED,
      ],
    }),
  ]
}

// Configured resources that have never been restored — all NO_STATUS, so
// successCount/inProgressCount/failedCount are all 0 while totalCount > 0.
// Drives the NEVER_ATTEMPTED "waiting / preparing" copy without a ratio.
function resourcesAllNoStatus(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.NO_STATUS }),
  ]
}

// Job-level restore failure: one resource landed before the job died, the rest
// never processed. No FAILED rows (failedCount = 0) so the status falls through
// to the initialRestoreFailed / failedToApplyBackup / restoreFailed copy, with a
// partial ratio (1/4).
function resourcesPartialNoFailure(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'reporting-fn', type: ResourceType.lambdaFunction, status: ResourceStatus.NO_STATUS }),
  ]
}

// All resources restored (Azure). Backs the "100% restored" reading while a sibling
// app in the same region-pair group is still finishing, so the conclude button is
// disabled despite the success status.
function resourcesAzureAllSucceeded(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'app-vm-01', type: ResourceType.azureVirtualMachine, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'app-data-disk', type: ResourceType.azureDisk, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'app-sql', type: ResourceType.azureSqlDatabase, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'app-vnet', type: ResourceType.azureVirtualNetwork, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesFailbackAvailable(): ViewableApplicationResource[] {
  // NEVER_ATTEMPTED — all rows show NO_STATUS. Only supportsFailback types
  // appear (EC2 / RDS / VPC). FailbackResource implements ViewableApplicationResource
  // so the test page POJO can stand in for the table renderer.
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.NO_STATUS }),
  ]
}

function resourcesFailbackInProgress(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({
      name: 'web-app-02',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.PROCESSING,
      progress: 0.5,
    }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.PENDING }),
  ]
}

function resourcesFailbackAllReturned(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesFailbackPartialFailure(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({
      name: 'web-app-02',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.FAILED,
      message: 'Instance type not available in source region.',
    }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
  ]
}

// Operation-notice presets — these mirror what ApplicationComponent's
// getters would produce given a particular sync phase / sandbox / quarantine
// configuration. Precomputed so the test page doesn't have to re-derive them.
const FAILOVER_NOTICE: RecoveryScreenOperationNotice = {
  recoveryPointTimeString: '03:14 PM on May 8',
  showNetworkSandboxStatus: false,
  networkSandboxStatus: 'disabled',
  showSandboxEventsLink: false,
  showRansomwareRecoveryStatus: true,
  ransomwareRecoveryStatus: 'disabled',
  domainAllowlistStatus: '',
  whitelistStatus: '',
}

const FAILOVER_NOTICE_QUARANTINED: RecoveryScreenOperationNotice = {
  ...FAILOVER_NOTICE,
  ransomwareRecoveryStatus: 'enabled',
}

const FAILOVER_TEST_NOTICE_BASIC: RecoveryScreenOperationNotice = {
  recoveryPointTimeString: '03:14 PM on May 8',
  showNetworkSandboxStatus: true,
  networkSandboxStatus: 'enabled',
  showSandboxEventsLink: false,
  showRansomwareRecoveryStatus: false,
  ransomwareRecoveryStatus: 'disabled',
  domainAllowlistStatus: '',
  whitelistStatus: 'Outbound access CIDRs: 10.0.0.0/16, 192.168.1.0/24',
}

const FAILOVER_TEST_NOTICE_ADVANCED: RecoveryScreenOperationNotice = {
  recoveryPointTimeString: '03:14 PM on May 8',
  showNetworkSandboxStatus: true,
  networkSandboxStatus: 'enabled',
  showSandboxEventsLink: true,
  showRansomwareRecoveryStatus: false,
  ransomwareRecoveryStatus: 'disabled',
  domainAllowlistStatus: 'Outbound access domains: api.snapcraft.io, github.com, pkg.go.dev, pypi.org',
  whitelistStatus: 'Outbound access CIDRs: 10.0.0.0/16',
}

// ProcessingStatus is never null-checked deeply by app-resources-view —
// passing null keeps the mock minimal and matches what the real component
// passes during pre-load. The few branches that read appStatus.inProgress
// fall through cleanly when the input is null.

// disabledActionReason tooltip strings — copied verbatim from
// ApplicationComponent.disabledActionReason / concludingToStandbyTooltip so the
// preview shows the exact production copy when an action is disabled.
export const ACTION_DISABLED_FAILBACK_IN_PROGRESS =
  'A failback is in progress — actions are unavailable until it completes.'
export const ACTION_DISABLED_CONCLUDING_TO_STANDBY =
  'This application is being restored back to standby — this action will be available once the restore completes.'
export const ACTION_DISABLED_RESTORE_IN_PROGRESS =
  'A restore is in progress — actions are unavailable until it completes.'
// Shown only when appGroupRestore is NOT entitled — a STANDBY refresh restore then
// disables the buttons. With the entitlement (the standard case) buttons stay enabled.
export const ACTION_DISABLED_STANDBY_REFRESH =
  'A standby refresh is in progress — actions are unavailable until it completes.'
// Failback button's own disabled tooltip (no failback-supporting resources),
// shown only when disabledActionReason is null. Mirrors failbackButtonDisabledTooltip.
export const FAILBACK_NO_RESOURCES_TOOLTIP = 'No resources in this recovery point support failback'
// Azure-only: a region-pair restore is still running for sibling apps, so this app
// can read 100% while its conclude/cancel buttons stay disabled.
export const ACTION_DISABLED_AZURE_REGION_PAIR =
  'A recovery is still in progress for other apps with the same primary and recovery regions. This action will be available once all of its applications finish restoring.'

// Tab-bar-flag presets per phase. Each call returns a fresh object so
// scenarios can splat-override individual flags without mutating siblings.
// `disabledActionReason` mirrors application.component.ts: per-phase buttons
// stay rendered during in-flight ops and are disabled with a tooltip rather
// than collapsing the row to a spinner. STANDBY refresh restores keep buttons
// enabled when appGroupRestore is entitled (the standard case), so the default
// is null — pass a reason only for FAILOVER restore / failback / conclude.
function tabBarStandby(
  opts: { stagingSuccess?: boolean; appIsDeleted?: boolean; disabledActionReason?: string | null } = {}
): RecoveryScreenTabBarFlags {
  const { stagingSuccess = true, appIsDeleted = false, disabledActionReason = null } = opts
  return {
    disabledActionReason,
    showSandboxSettings: false,
    showTestButton: !appIsDeleted,
    showRecoverButton: !appIsDeleted,
    testRecoverDisabled: !stagingSuccess,
    showUnquarantine: false,
    showFailback: false,
    failbackDisabled: false,
    showConcludeRecovery: false,
    showConcludeTest: false,
    isEarlyStopTest: false,
  }
}

function tabBarFailover(
  opts: {
    quarantined?: boolean
    hasFailbackResources?: boolean
    showFailback?: boolean
    disabledActionReason?: string | null
  } = {}
): RecoveryScreenTabBarFlags {
  const { quarantined = false, hasFailbackResources = true, showFailback = true, disabledActionReason = null } = opts
  return {
    disabledActionReason,
    showSandboxSettings: false,
    showTestButton: false,
    showRecoverButton: false,
    testRecoverDisabled: false,
    showUnquarantine: quarantined,
    // Production gates failback on entitled + !quarantined + canRecover. In the
    // quarantined scenario it disappears entirely (UNQUARANTINE takes its slot).
    showFailback: showFailback && !quarantined,
    failbackDisabled: !hasFailbackResources,
    showConcludeRecovery: true,
    showConcludeTest: false,
    isEarlyStopTest: false,
  }
}

function tabBarFailoverTest(
  opts: { sandboxEnabled?: boolean; isEarlyStop?: boolean; disabledActionReason?: string | null } = {}
): RecoveryScreenTabBarFlags {
  const { sandboxEnabled = false, isEarlyStop = false, disabledActionReason = null } = opts
  return {
    // FAILOVER_TEST keeps its buttons enabled during a restore — only a failback
    // in flight disables them (the conclude dialog handles cancel-vs-conclude).
    disabledActionReason,
    showSandboxSettings: sandboxEnabled,
    showTestButton: false,
    showRecoverButton: false,
    testRecoverDisabled: false,
    showUnquarantine: false,
    showFailback: false,
    failbackDisabled: false,
    showConcludeRecovery: false,
    showConcludeTest: true,
    isEarlyStopTest: isEarlyStop,
  }
}

export function buildRecoveryScreenScenarios(): RecoveryScreenScenario[] {
  // Each scenario constructs its own mock application so syncPhase / sandbox
  // flags align with the screen state. Cheap — buildMockApplication is a
  // plain POJO factory.
  const standbyApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.STANDBY, ...overrides })
  const failoverApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.FAILOVER, ...overrides })
  const failoverTestApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.FAILOVER_TEST, ...overrides })
  const deletedApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.STANDBY, ...overrides })

  return [
    // ─── Recovery — STANDBY ────────────────────────────────────────
    {
      key: 'r1-pre-first-backup',
      label: 'Pre-first-backup (waiting)',
      description: 'No backup has succeeded yet. Resource table is empty; status bar tells the user to wait.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.waitingForFirstBackup,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'r2-initial-restore-in-progress',
      label: 'Performing initial restore (PENDING + Confirming)',
      description:
        'First restore in flight. Rows include a PENDING-with-confirming-substatus to show the post-job-lag bridge.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.performingInitialRestore,
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      // Initial restore is automatic; with appGroupRestore the standby buttons
      // stay enabled (just greyed by !lastStagingSuccess until staging lands).
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'r3-steady-state-success',
      label: 'Steady state (with pilot light + orphaned target)',
      description:
        'Fully restored standby. Includes a pilot-light row (source visible, target blank) and an orphaned-target row.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesSteadyState(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.successfullyRestored('1 hour ago'),
        // The orphaned-target row stays NO_STATUS (not a success), so 5 of 6 count.
        ratioText: `83% restored (5/6)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r4-mixed-failures',
      label: 'Mixed failures (NOT_PROCESSED clickable)',
      description: 'One FAILED parent + two NOT_PROCESSED rows the user can click through to the upstream failure.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesMixedFailures(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.failedToRestoreCount(1, '1 hour ago'),
        ratioText: `40% restored (2/5)${RP_AT_3_14_MAY_8}.`,
        failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('1 hour ago'), count: 1 },
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r5-blocked-by-dependency',
      label: 'Blocked by dependency (UNSATISFIED_DEPENDENCY)',
      description: 'Transient blocked state — two rows waiting on db-primary, db-primary itself still PROCESSING.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBlocked(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoringBackup('5 minutes ago'),
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        progressCount: 1,
        progressTotal: 4,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r6-sibling-blocked',
      label: 'Sibling failover blocking standby restore',
      description:
        'Standby restore is queued because a sibling app sharing the region pair is in FAILOVER. Table is empty.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.standbyRestoreBlocked('us-east-1', 'us-west-2'),
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r7-eks-needs-attention',
      label: 'EKS parent with failed child (needs-attention rollup)',
      description:
        'EKS cluster + 4 pods, one FAILED, one still PROCESSING. The PROCESSING child keeps the overall status "Restoring..." while the FAILED child drives the parent needs-attention pill.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesEksWithFailedChild(),
      // While any child is in progress, production keeps the status INDETERMINATE
      // (statusText.ts: inProgressCount > 0 wins over the failed count). The
      // needs-attention pill is computed independently by app-resources-view.
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoringBackup('1 hour ago'),
        ratioText: `66% restored (4/6)${RP_AT_3_14_MAY_8}.`,
        progressCount: 4,
        progressTotal: 6,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },

    // ─── Recovery — FAILOVER ───────────────────────────────────────
    {
      key: 'r8-failover-in-progress',
      label: 'Restoring for recovery (AWS)',
      description:
        'AWS active recovery, mid-restore. CONCLUDE RECOVERY stays rendered but disabled with the restore-in-progress tooltip.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoreInProgress(false),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      // FAILOVER restore mid-flight: CONCLUDE RECOVERY stays rendered but disabled
      // with the restore-in-progress tooltip.
      tabBarFlags: tabBarFailover({
        hasFailbackResources: false,
        disabledActionReason: ACTION_DISABLED_RESTORE_IN_PROGRESS,
      }),
    },
    {
      key: 'r9-failover-complete',
      label: 'All resources restored (FAILBACK eligible)',
      description: 'Active recovery complete. In production this state lights up the FAILBACK button.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesSteadyState(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', false),
        ratioText: `83% restored (5/6)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'r10-failover-quarantined',
      label: 'Recovery with quarantine mode ON',
      description: 'Active recovery with Quarantine Mode enabled. Notice shows quarantine state inline.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesMixedFailures(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.failedToRestoreCount(1, '15 minutes ago'),
        ratioText: `40% restored (2/5)${RP_AT_3_14_MAY_8}.`,
        failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('15 minutes ago'), count: 1 },
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE_QUARANTINED,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ quarantined: true }),
    },
    {
      key: 'r10b-failover-azure-region-pair',
      label: 'Restoring for recovery — region-pair wait (Azure)',
      description:
        "Azure recovery where this app's resources are all restored but a sibling app in the same region-pair group is still going. CONCLUDE RECOVERY + FAILBACK stay rendered but disabled with the region-pair tooltip.",
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp({ type: ApplicationType.AZURE }),
      resources: resourcesAzureAllSucceeded(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', false),
        ratioText: `100% restored (359/359)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ disabledActionReason: ACTION_DISABLED_AZURE_REGION_PAIR }),
    },

    // ─── Recovery — FAILOVER_TEST ──────────────────────────────────
    {
      key: 'r11-test-in-progress-advanced',
      label: 'Test recovery in progress (advanced sandbox)',
      description: 'Test cycle mid-restore. Notice shows sandbox ON with the "See most recent events" link visible.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true, advancedNetworkSandbox: true }),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoreInProgress(true),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_ADVANCED,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true }),
    },
    {
      key: 'r12-test-complete-basic',
      label: 'Test recovery complete (basic sandbox)',
      description: 'Test cycle finished. Basic sandbox shows the CIDR allowlist row but no domains list.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true }),
      resources: resourcesSteadyState(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', true),
        ratioText: `83% restored (5/6)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true }),
    },
    {
      key: 'r12b-test-in-progress-aws',
      label: 'Test recovery in progress (AWS)',
      description:
        'AWS test cycle mid-restore. CONCLUDE TEST stays enabled (shows CANCEL TEST early-stop) — the conclude dialog routes cancel-vs-conclude per app. Contrast with the Azure region-pair case.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true }),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoreInProgress(true),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true, isEarlyStop: true }),
    },
    {
      key: 'r12c-test-azure-region-pair',
      label: 'Test recovery — region-pair wait (Azure)',
      description:
        'The reported bug: Azure test recovery reads 100% restored while a sibling app in the same region-pair group is still restoring. CONCLUDE TEST + SANDBOX SETTINGS stay rendered but disabled with the region-pair tooltip instead of vanishing.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ type: ApplicationType.AZURE, networkSandboxEnabled: true }),
      resources: resourcesAzureAllSucceeded(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.allResourcesRestored('43 minutes ago', true),
        ratioText: `100% restored (359/359)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({
        sandboxEnabled: true,
        disabledActionReason: ACTION_DISABLED_AZURE_REGION_PAIR,
      }),
    },

    // ─── Recovery — Cleanup (deleted app) ──────────────────────────
    {
      key: 'r13-deletion-cleanup',
      label: 'Cleaning up recovery environment',
      description: 'App marked deleted; cleanup in progress. Mirrors the override that suppresses phase-based status.',
      tab: 'recovery',
      group: 'CLEANUP',
      syncPhase: SyncPhaseDto.STANDBY,
      application: deletedApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.cleaningUp,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      // Deleted app hides TEST / LAUNCH RECOVERY entirely, so there's nothing to disable.
      tabBarFlags: tabBarStandby({ appIsDeleted: true }),
    },
    {
      key: 'r14-concluding-to-standby',
      label: 'Concluding recovery — winding back to standby',
      description:
        'A recovery was just concluded; the app is restoring back to standby. TEST / LAUNCH RECOVERY stay rendered but disabled with the wind-down tooltip until the restore completes.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoringBackup('5 minutes ago'),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      // isConcludingToStandby — buttons disabled with the conclude-to-standby tooltip.
      tabBarFlags: tabBarStandby({ disabledActionReason: ACTION_DISABLED_CONCLUDING_TO_STANDBY }),
    },

    // ─── Recovery — STANDBY (additional status variants) ───────────
    {
      key: 'r15-first-backup-in-progress',
      label: 'Initial backup in progress (restore queued)',
      description:
        'No backup has ever succeeded and one is actively running. Restore to standby will start automatically once it lands.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.firstBackupInProgress,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'r16-waiting-initial-restore',
      label: 'Waiting to perform initial restore',
      description:
        'First backup has succeeded; resources are configured but the initial standby restore has not started yet (all NO_STATUS).',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesAllNoStatus(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.waitingForInitialRestore,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r17-restored-with-issues',
      label: 'Successfully restored — with open issues',
      description:
        'Standby restore complete but issues are open against the recovery point. State is WARNING; the issues link sits next to the headline.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesSteadyState(),
      status: {
        state: AppStatusState.WARNING,
        text: STATUS_TEXT.recovery.successfullyRestored('1 hour ago'),
        ratioText: `83% restored (5/6)${RP_AT_3_14_MAY_8}.`,
        issues: [{} as any],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r18-initial-restore-failed',
      label: 'Initial restore to standby failed',
      description:
        'First-ever standby restore failed at the job level (no per-resource FAILED rows). Distinct "Initial restore..." copy.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesPartialNoFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.initialRestoreFailed,
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r19-failed-to-apply-backup',
      label: 'Subsequent restore to standby failed',
      description:
        'A prior standby restore succeeded; the latest one failed at the job level. Uses the "Failed to restore backup..." copy rather than the initial-restore copy.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesPartialNoFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.failedToApplyBackup('1 hour ago'),
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r20-standby-refresh-disabled',
      label: 'Standby refresh — actions disabled (no appGroupRestore)',
      description:
        'A STANDBY refresh restore is in flight on an account WITHOUT the appGroupRestore entitlement, so the action buttons disable with the standby-refresh tooltip. (With the entitlement they stay enabled — see r2.)',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoringBackup('5 minutes ago'),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ disabledActionReason: ACTION_DISABLED_STANDBY_REFRESH }),
    },

    // ─── Recovery — FAILOVER (additional status variants) ──────────
    {
      key: 'r21-failover-preparing',
      label: 'Preparing to restore for recovery',
      description: 'Recovery launched but no resource has begun restoring yet (NEVER_ATTEMPTED). No ratio shown.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.preparingRestore(false),
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ hasFailbackResources: false }),
    },
    {
      key: 'r22-failover-restore-failed',
      label: 'Restore for recovery failed (job-level)',
      description:
        'Recovery restore failed at the job level with no per-resource FAILED rows. Uses the "Restore for recovery failed." copy.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesPartialNoFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.restoreFailed(false),
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ hasFailbackResources: false }),
    },

    // ─── Recovery — FAILOVER_TEST (additional status variants) ─────
    {
      key: 'r23-test-preparing',
      label: 'Preparing to restore for test recovery',
      description: 'Test recovery launched but no resource has begun restoring yet (NEVER_ATTEMPTED). No ratio shown.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true }),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.preparingRestore(true),
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true }),
    },
    {
      key: 'r24-test-restore-failed',
      label: 'Restore for test recovery failed (job-level)',
      description:
        'Test recovery restore failed at the job level with no per-resource FAILED rows. Uses the "Restore for test recovery failed." copy.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true }),
      resources: resourcesPartialNoFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.restoreFailed(true),
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true }),
    },

    // ─── Recovery — Cleanup (additional status variants) ───────────
    {
      key: 'r25-finalizing-deletion',
      label: 'Finalizing application deletion',
      description: 'App marked deleted and all recovery resources have been cleaned up; deletion is finalizing.',
      tab: 'recovery',
      group: 'CLEANUP',
      syncPhase: SyncPhaseDto.STANDBY,
      application: deletedApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.finalizingDeletion,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ appIsDeleted: true }),
    },
    {
      key: 'r26-cleanup-failed',
      label: 'Recovery environment cleanup failed',
      description: 'App marked deleted but cleanup hit an error (restore issues present). State is ERROR.',
      tab: 'recovery',
      group: 'CLEANUP',
      syncPhase: SyncPhaseDto.STANDBY,
      application: deletedApp(),
      resources: resourcesMixedFailures(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.cleanupFailed,
        issues: [{} as any],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ appIsDeleted: true }),
    },

    // ─── Failback ──────────────────────────────────────────────────
    {
      key: 'f1-no-supported',
      label: 'No resources support failback',
      description: 'Account is entitled, but the app has no failback-supporting resource types.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INFO,
        text: STATUS_TEXT.failback.noSupported,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ hasFailbackResources: false }),
    },
    {
      key: 'f2-available',
      label: 'N resources available for failback',
      description:
        'Failback has never been attempted. All rows NO_STATUS — the FAILBACK button is enabled in production.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackAvailable(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.failback.available(3),
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'f3-in-progress',
      label: 'First failback attempt in progress',
      description: 'Failback running. Status bar shows ratio; operation notice surfaces the running-state copy.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackInProgress(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.failback.returning,
        ratioText: '33% returned (1/3).',
        progressCount: 1,
        progressTotal: 3,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: 'Failback is returning resources to the primary environment.',
      tabBarFlags: tabBarFailover({ disabledActionReason: ACTION_DISABLED_FAILBACK_IN_PROGRESS }),
    },
    {
      key: 'f4-all-returned',
      label: 'All resources returned',
      description: 'Successful failback complete. Notice surfaces the success copy.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackAllReturned(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.failback.allReturned,
        ratioText: '100% returned (3/3).',
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: 'Failback complete. Resources have been returned to the primary environment.',
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'f5-partial-failure',
      label: 'Partial failure — N resources failed to return',
      description:
        "The most recent failback attempt failed with one resource left un-returned (MOST_RECENT_FAILED). No completion notice shows because the last attempt didn't succeed. Clickable failed-link in the status bar filters the table.",
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackPartialFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.failback.failedToReturnCount(1),
        // floor(2/3 * 100) = 66, matching computeFailbackStatus's Math.floor ratio.
        ratioText: '66% returned (2/3).',
        failedLink: { ...STATUS_TEXT.failback.failedLinkReturn, count: 1 },
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'f6-retry-in-progress',
      label: 'Retrying failback (SUBSEQUENT_ATTEMPT)',
      description: 'A previous failback attempt failed; a retry is now running.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackInProgress(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.failback.retrying,
        // resourcesFailbackInProgress has 1 SUCCEEDED of 3 → 33%, matching the table.
        ratioText: '33% returned (1/3).',
        progressCount: 1,
        progressTotal: 3,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: 'Failback is returning resources to the primary environment.',
      tabBarFlags: tabBarFailover({ disabledActionReason: ACTION_DISABLED_FAILBACK_IN_PROGRESS }),
    },
    {
      key: 'f7-job-failure',
      label: 'Terminal job failure (no resource-level failures)',
      description: 'Failback job itself failed without individual resource failures.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackAvailable(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.failback.failed,
        // Production always emits a ratio when totalCount > 0, even on terminal failure.
        ratioText: '0% returned (0/3).',
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover(),
    },
  ]
}

// Resource tab enum re-export so the test page can bind to the failback tab
// type when wiring the screen preview without importing ResourcesTab itself.
export const RECOVERY_SCREEN_TABS = {
  recovery: ResourcesTab.RECOVERY_ENVIRONMENT,
  failback: ResourcesTab.FAILBACK,
} as const

// -------------------------------------------------------------------------
// Backup screen scenarios (ARP-7143 — Backup tab counterpart to Recovery)
// -------------------------------------------------------------------------
//
// Same scenario-driven snapshot pattern as the Recovery screen section, but
// scoped to the Backup tab. Differences from Recovery:
//   - No sub-tabs (Backup has no Recovery/Failback chips)
//   - No operation-status notice (Backup tab doesn't render one in production)
//   - Resource table uses StatusContext.BACKUP (pill labels: Backing Up / Ready / Failed)
//   - Status object is BackupHealthStatus (carries optional priorBackupInfo)
//
// Tab-bar action gating is identical to Recovery — the action strip is
// persistent across both tabs in production, so we reuse RecoveryScreenTabBarFlags
// + the same tabBarStandby/tabBarFailover/tabBarFailoverTest helpers.

export interface BackupScreenScenario {
  key: string
  label: string
  description: string
  // optgroup label in the dropdown.
  group: 'STANDBY' | 'FAILOVER'
  syncPhase: SyncPhaseDto
  application: Application
  resources: ViewableApplicationResource[]
  status: BackupHealthStatus
  tabBarFlags: RecoveryScreenTabBarFlags
}

interface MockBackupResourceOpts {
  name: string
  type: ResourceType
  status: ResourceStatus
  progress?: number
  message?: string
  substatus?: string
  arn?: string
}

// ViewableApplicationResource POJO factory for Backup-context rows. Substatus
// rules mirror the production computeResourceSubstatus output for backup
// scope: INIT → "Waiting in queue..." (or "Confirming status..." during the
// post-job-lag bridge), PROCESSING → progress %, FAILED → message verbatim.
// Pilot-light / orphaned-target overrides don't apply to backup scope.
function mkBackupResource(opts: MockBackupResourceOpts): ViewableApplicationResource {
  const { name, type, status, progress = 0, message = null, substatus, arn } = opts

  const computedSubstatus = (() => {
    if (substatus !== undefined) return substatus
    switch (status) {
      case ResourceStatus.PROCESSING:
        return progress > 0 && progress < 1 ? `${Math.round(progress * 100)}%` : message || undefined
      case ResourceStatus.PENDING:
        return message || 'Waiting in queue...'
      case ResourceStatus.FAILED:
        return message || undefined
      case ResourceStatus.NOT_PROCESSED:
        return 'Upstream dependency failed'
      default:
        return undefined
    }
  })()

  return {
    resourceType: type,
    resourceVariant: null,
    resourceArn: (arn ?? `arn:aws:mock:us-east-1:123456789012:${type}/${name}`) as Arn,
    sourceResourceName: name,
    // Backup-context table only shows the source column meaningfully — target
    // is implicit (the backup destination). Production sometimes renders
    // target details too, but the mock can leave them empty without crashing.
    targetResourceName: '',
    resourceProperties: [],
    status,
    progress,
    message,
    substatus: computedSubstatus,
    issues: new Set(),
    displayResourceType: getResourceTypeDisplayString(type, null),
    resourceTypeDetails: getResourceTypeDetails({ resourceType: type }),
  } as ViewableApplicationResource
}

// Resource-set builders per scenario.
function resourcesBackupPlanning(): ViewableApplicationResource[] {
  // Queued resources whose timestamps haven't caught up. NO_STATUS dominates.
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.PENDING }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.NO_STATUS }),
  ]
}

function resourcesBackupInitial(): ViewableApplicationResource[] {
  // First-attempt-in-progress: many PROCESSING + a few PENDING in the queue.
  return [
    mkBackupResource({
      name: 'web-app-01',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.PROCESSING,
      progress: 0.3,
    }),
    mkBackupResource({
      name: 'web-app-02',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.PROCESSING,
      progress: 0.6,
    }),
    mkBackupResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.PROCESSING,
      progress: 0.15,
    }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.PENDING }),
    mkBackupResource({ name: 'reporting-fn', type: ResourceType.lambdaFunction, status: ResourceStatus.PENDING }),
    mkBackupResource({ name: 'orders', type: ResourceType.dynamoDbTable, status: ResourceStatus.PENDING }),
  ]
}

function resourcesBackupSubsequent(): ViewableApplicationResource[] {
  // Subsequent attempt: mix of PROCESSING (the resources being re-backed-up)
  // and SUCCEEDED (those already complete this cycle).
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.PROCESSING,
      progress: 0.7,
    }),
    mkBackupResource({
      name: 'app-data',
      type: ResourceType.s3Bucket,
      status: ResourceStatus.PROCESSING,
      progress: 0.4,
    }),
    mkBackupResource({ name: 'reporting-fn', type: ResourceType.lambdaFunction, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'orders', type: ResourceType.dynamoDbTable, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBackupSucceeded(): ViewableApplicationResource[] {
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'reporting-fn', type: ResourceType.lambdaFunction, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'orders', type: ResourceType.dynamoDbTable, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBackupScanResults(): ViewableApplicationResource[] {
  // Successful backup with malware-scan pills mixed in. SCAN_INFECTED on a
  // bucket, SCAN_FAILED on another, SCAN_CLEAN on a third, plain SUCCEEDED
  // for resources that don't get scanned.
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SCAN_CLEAN }),
    mkBackupResource({ name: 'user-uploads', type: ResourceType.s3Bucket, status: ResourceStatus.SCAN_INFECTED }),
    mkBackupResource({ name: 'archive-logs', type: ResourceType.s3Bucket, status: ResourceStatus.SCAN_FAILED }),
    mkBackupResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBackupFailed(): ViewableApplicationResource[] {
  // Mixed SUCCEEDED + FAILED, with a NOT_PROCESSED row blocked downstream of
  // the failure to show the upstream-dependency pill in backup context too.
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.FAILED,
      message: 'Snapshot quota exceeded in source region.',
    }),
    mkBackupResource({
      name: 'db-replica',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.NOT_PROCESSED,
    }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'orders', type: ResourceType.dynamoDbTable, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBackupJobLevelFailure(): ViewableApplicationResource[] {
  // Job itself failed before reaching any resource. Most rows show NO_STATUS
  // since the run never produced per-resource updates.
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.NO_STATUS }),
  ]
}

// Time-string + RP suffix helpers tuned for the Backup status text strings.
const RP_AT_1_14_MAY_8 = ' from recovery point at 01:14 PM on May 8'
const RP_AT_11_14_MAY_8 = ' from recovery point at 11:14 AM on May 8'
const FAILED_AT_10_25_MAY_9 = ' at 10:25 AM on May 9'

export function buildBackupScreenScenarios(): BackupScreenScenario[] {
  const standbyApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.STANDBY, ...overrides })
  const failoverApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.FAILOVER, ...overrides })

  return [
    // ─── Standby ────────────────────────────────────────────────────
    {
      key: 'b1-planning-backup',
      label: 'Planning backup (queued)',
      description:
        'NEVER_ATTEMPTED state with queued resources whose timestamps have not caught up. Bridges to first-attempt-in-progress.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupPlanning(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.backup.planningBackup,
        issues: [],
      },
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'b2-initial-backup-in-progress',
      label: 'Creating initial backup',
      description:
        'First-attempt-in-progress. Inline progress bar (N/M resources). No prior successful backup yet so TEST + LAUNCH RECOVERY remain disabled.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupInitial(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.backup.creatingInitial,
        // resourcesBackupInitial has 0 SUCCEEDED of 6 (3 PROCESSING + 3 PENDING).
        progressCount: 0,
        progressTotal: 6,
        issues: [],
      },
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'b3-subsequent-backup-in-progress',
      label: 'Creating subsequent backup',
      description:
        'A backup is running with a prior successful backup on record. Status bar shows progress bar AND the prior-backup summary at far right.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupSubsequent(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.backup.creatingNew,
        progressCount: 4,
        progressTotal: 6,
        priorBackupInfo: STATUS_TEXT.backup.priorBackupInfo('2 hours ago', RP_AT_1_14_MAY_8, 6),
        issues: [],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b4-last-successful-clean',
      label: 'Last successful — clean',
      description: 'Steady-state successful backup. All rows SUCCEEDED, no issues.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupSucceeded(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', `${RP_AT_1_14_MAY_8}.`),
        issues: [],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b5-last-successful-with-issues',
      label: 'Last successful — with open issues',
      description:
        'Successful backup but issues are open. Status state is WARNING; the "Review and fix N issues" link appears next to the headline.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupSucceeded(),
      status: {
        state: AppStatusState.WARNING,
        text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', `${RP_AT_1_14_MAY_8}.`),
        issues: [{} as any, {} as any],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b6-scan-results-mixed',
      label: 'Last successful — with malware-scan results',
      description:
        'Successful backup with mixed scan-result pills (clean / infected / scan failed). WARNING because at least one bucket is infected.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupScanResults(),
      status: {
        state: AppStatusState.WARNING,
        text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', `${RP_AT_1_14_MAY_8}.`),
        issues: [{} as any],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b7-failed-n-resources',
      label: 'Failed N resources',
      description:
        'Per-resource failures: status bar shows clickable failed-link with the count and the failure timestamp. Prior backup info renders in the substatus slot.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupFailed(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.backup.failedToBackupCount(1, FAILED_AT_10_25_MAY_9),
        failedLink: { ...STATUS_TEXT.backup.failedLinkBackup, count: 1, suffix: `${FAILED_AT_10_25_MAY_9}.` },
        priorBackupInfo: STATUS_TEXT.backup.lastSuccessful('1 day ago', `${RP_AT_11_14_MAY_8}.`),
        issues: [],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b8-job-level-failure',
      label: 'Failed to create backup (job-level)',
      description:
        'Job itself failed without any individual resource failures. No clickable failed-link. Prior-backup info renders in the substatus slot.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupJobLevelFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.backup.failedToCapture(FAILED_AT_10_25_MAY_9),
        priorBackupInfo: STATUS_TEXT.backup.lastSuccessful('1 day ago', `${RP_AT_11_14_MAY_8}.`),
        issues: [],
      },
      tabBarFlags: tabBarStandby(),
    },

    // ─── Failover (frozen last-known) ───────────────────────────────
    {
      key: 'b9-frozen-during-failover',
      label: 'Frozen last-known result during failover',
      description:
        'App is in FAILOVER — Backup tab shows the last-known result from before the failover (no active backup polling).',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesBackupSucceeded(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.backup.lastSuccessful('3 hours ago', ` from recovery point at 12:14 PM on May 8.`),
        issues: [],
      },
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'b10-no-resources-configured',
      label: 'No resources configured (empty)',
      description:
        'Brand-new app with no resources configured. With an empty backup resource list, production short-circuits to this INFO placeholder before any status computation; resource table is empty.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: [],
      status: {
        // Verbatim from application.component.ts: the empty-resource guard emits this
        // (not STATUS_TEXT.backup.noDataAvailable) when backupResources.length === 0.
        state: AppStatusState.INFO,
        text: 'No data available yet. Configure your resources to create initial backup.',
        issues: [],
      },
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },

    // ─── Failover (frozen last-known) ───────────────────────────────
    {
      key: 'b11-no-backup-data',
      label: 'No backup data available',
      description:
        'Resources exist (all NO_STATUS) but no backup has ever produced a recovery point, during a non-standby phase. computeBackupHealthLastKnown surfaces the noDataAvailable placeholder as INFO (no spinner glyph).',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesBackupJobLevelFailure(),
      status: {
        state: AppStatusState.INFO,
        text: STATUS_TEXT.backup.noDataAvailable,
        issues: [],
      },
      tabBarFlags: tabBarFailover(),
    },
  ]
}
